import React, { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { motion } from "framer-motion";
import { BookOpen, ChevronDown, ListOrdered, Loader2, Mic, MicOff, PanelRightOpen, Paperclip, Send, Sparkles, Square, UploadCloud, Video, Volume2, VolumeX, X } from "lucide-react";
import "./App.css";
import AuthView from "./components/AuthView";
import MessageItem, { getMediaUrls } from "./components/MessageItem";
import SettingsModal from "./components/SettingsModal";
import Sidebar from "./components/Sidebar";
import ProfilePage from "./components/ProfilePage";
import UpdatesPage from "./components/UpdatesPage";
import DocsPage from "./components/DocsPage";
import ModelPicker from "./components/ModelPicker";
import ExecutionProgress from "./components/ExecutionProgress";
import { taskTimelineMessages } from "./components/taskTimeline";
import ComposerQueue from "./components/ComposerQueue";
import { queuedJobId, readQueue, useComposerQueue, withQueueLock } from "./components/composerQueueState";
import UpdateNotice from "./components/UpdateNotice";
import { RouteTransition, SessionBootstrap } from "./components/PageTransitions";
import { announcementMuted, muteAnnouncements, clearAnnouncementPreferences } from "./announcementPreferences";
import { API_BASE, ICON_CONFIG } from "./config";
import { revealTheme } from "./themeTransition";
import "./theme.css";

let sessionExpiryRedirecting = false;

const SHARED_THEME_COOKIE = "lumanova_theme";
const readChatThemeMode = () => {
  try { const mode = window.localStorage.getItem("lumanova_chat_theme_mode"); return ["system","light","dark"].includes(mode) ? mode : null; }
  catch { return null; }
};

const readSharedTheme = () => {
  if (typeof document === "undefined") return null;
  const value = document.cookie
    .split(";")
    .map((entry) => entry.trim())
    .find((entry) => entry.startsWith(`${SHARED_THEME_COOKIE}=`))
    ?.split("=")[1];
  return value === "dark" || value === "light" ? value : null;
};

const writeSharedTheme = (theme) => {
  if (typeof document === "undefined") return;
  const sharedDomain = (window.location.hostname === "lumanova.icu" || window.location.hostname.endsWith(".lumanova.icu"))
    ? "; Domain=.lumanova.icu"
    : "";
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${SHARED_THEME_COOKIE}=${theme}; Path=/${sharedDomain}${secure}; SameSite=Lax; Max-Age=31536000`;
};

const apiFetch = async (url, options = {}) => {
  const response = await fetch(url, { ...options, credentials: "include" });
  const endpoint = String(url || "");
  if (
    response.status === 401
    && !endpoint.endsWith("/auth/session")
    && !sessionExpiryRedirecting
    && typeof window !== "undefined"
  ) {
    clearAnnouncementPreferences();
    sessionExpiryRedirecting = true;
    window.location.assign("/");
  }
  return response;
};

const loadAccountActivity = async () => {
  const response = await apiFetch(`${API_BASE}/v1/account/activity`);
  if (!response.ok) throw new Error("Activity unavailable");
  const data = await response.json();
  if (!Array.isArray(data.days) || !Number.isFinite(data.tracking_started_at)) throw new Error("Invalid activity response");
  return data;
};

export const normalizeModels = (data) => {
  const models = Array.isArray(data?.data) ? data.data : data?.data?.data;
  return Array.isArray(models) ? models : [];
};

const isMobileViewport = () => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  const viewportWidth = Math.min(
    window.innerWidth || Number.POSITIVE_INFINITY,
    window.visualViewport?.width || Number.POSITIVE_INFINITY,
    document.documentElement?.clientWidth || Number.POSITIVE_INFINITY
  );
  const isNarrow = viewportWidth <= 900;
  const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;
  return isNarrow || (isTouchDevice && viewportWidth <= 1180);
};

const downsampleToPcm16 = (input, inputRate, outputRate = 16000) => {
  if (inputRate === outputRate) {
    const pcm = new Int16Array(input.length);
    for (let index = 0; index < input.length; index += 1) {
      const sample = Math.max(-1, Math.min(1, input[index]));
      pcm[index] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
    }
    return pcm.buffer;
  }
  const ratio = inputRate / outputRate;
  const outputLength = Math.floor(input.length / ratio);
  const pcm = new Int16Array(outputLength);
  for (let outputIndex = 0; outputIndex < outputLength; outputIndex += 1) {
    const sourceIndex = Math.min(input.length - 1, Math.floor(outputIndex * ratio));
    const sample = Math.max(-1, Math.min(1, input[sourceIndex]));
    pcm[outputIndex] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }
  return pcm.buffer;
};

const TTS_SESSION_PREFS_KEY = "lumanova_tts_session_prefs_v1";
const TTS_UNLOCK_AUDIO_URL = "data:audio/wav;base64,UklGRiUAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQEAAACA";
const PHONE_RE = /^1\d{10}$/;

const readTtsSessionPrefs = () => {
  if (typeof window === "undefined") return {};
  try {
    const value = JSON.parse(window.localStorage.getItem(TTS_SESSION_PREFS_KEY) || "{}");
    return value && typeof value === "object" ? value : {};
  } catch {
    return {};
  }
};

const VoiceLiveOverlay = React.memo(function VoiceLiveOverlay({
  canceling,
  sending,
  overlayRef,
  cancelTargetRef,
  sendTargetRef,
  waveformRef,
  transcriptRef,
}) {
  useEffect(() => {
    if (!transcriptRef.current) return;
    transcriptRef.current.textContent = "请开始说话...";
    transcriptRef.current.classList.add("placeholder");
  }, [transcriptRef]);

  return (
    <div
      ref={overlayRef}
      className={`voice-live-overlay ${canceling ? "canceling" : ""} ${sending ? "sending" : ""}`}
      role="status"
    >
      <div className="voice-live-panel">
        <div className="voice-live-kicker">
          <span aria-hidden="true" />
          正在听
        </div>
        <div ref={transcriptRef} className="voice-live-transcript placeholder" aria-live="polite" />
        <canvas ref={waveformRef} className="voice-live-wave-canvas" aria-hidden="true" />
      </div>

      <div className="voice-live-controls" aria-hidden="true">
        <div ref={cancelTargetRef} className="voice-live-cancel-target">
          <span className="voice-live-cancel-icon">
            <X size={16} />
          </span>
          <span className="voice-live-cancel-copy">取消</span>
        </div>
        <div ref={sendTargetRef} className="voice-live-send-target">
          <strong>松开发送</strong>
        </div>
      </div>
    </div>
  );
});

const extractResponseMessage = (data, fallback = "请求失败，请稍后再试") => {
  if (!data) return fallback;
  if (typeof data === "string") return data;
  if (typeof data.msg === "string" && data.msg.trim()) return data.msg;
  if (typeof data.message === "string" && data.message.trim()) return data.message;
  if (typeof data.detail === "string" && data.detail.trim()) return data.detail;
  if (Array.isArray(data.detail) && data.detail.length) {
    return data.detail.map((item) => item?.msg || item?.message).filter(Boolean).join("；") || fallback;
  }
  return fallback;
};

const toolLabels = {
  weather_current: "查询实时天气",
  generate_image: "正在生成图片",
  image_edit: "正在编辑图片",
  web_search: "正在搜索网页",
  rag_search: "正在检索知识库",
  save_subject_spec: "正在整理主体设定",
  generate_subject_design: "正在生成主体设计图",
  inspect_subject_design: "正在检查主体素材",
  revise_subject_design: "正在修改主体设计",
  approve_subject_design: "正在定稿主体素材",
  generate_subject_preview: "正在生成场景效果图",
  save_video_concept: "正在整理视频概念",
  approve_video_concept: "正在确认视频方案",
  plan_video_keyframes: "正在规划视频关键帧",
  generate_video_keyframe: "正在生成视频关键帧",
  inspect_video_keyframe: "正在检查关键帧素材",
  approve_video_keyframe: "正在确认关键帧素材",
  start_mbti_test: "正在开始 MBTI 测试",
  answer_question: "正在提交测试回答",
  get_progress: "正在查询测试进度",
  "campaign-calendar": "正在查询麦当劳活动",
  "query-nearby-stores": "正在查询附近门店",
  "delivery-query-addresses": "正在查询配送地址",
  "delivery-query-stores": "正在查询配送门店",
  "delivery-create-address": "正在添加配送地址",
  "query-meals": "正在查询麦当劳餐品",
  "query-meal-detail": "正在查询餐品详情",
  "query-meal-assistance": "正在查询团餐服务",
  "list-nutrition-foods": "正在查询餐品营养",
  "available-coupons": "正在查询可领优惠券",
  "query-my-coupons": "正在查询我的优惠券",
  "query-store-coupons": "正在查询门店优惠券",
  "auto-bind-coupons": "正在领取优惠券",
  "calculate-price": "正在计算订单价格",
  "create-order": "正在创建麦当劳订单",
  "query-order": "正在查询订单详情",
  "order-list": "正在查询历史订单",
  "query-my-account": "正在查询积分账户",
  "mall-points-products": "正在查询积分商品",
  "mall-product-detail": "正在查询积分商品详情",
  "mall-create-order": "正在创建积分兑换订单",
  "mall-order-list": "正在查询积分订单",
  "mall-order-detail": "正在查询积分订单详情",
  "now-time-info": "正在确认当前时间",
  calculate_mbti_result: "正在生成 MBTI 结果",
};

const latestNewAssistantAnimationKey = (sessionId, messages = [], knownMessageIds = new Set()) => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (
      message?.role === "assistant"
      && message?.content
      && message?.message_type !== "interrupted"
      && !knownMessageIds.has(message.message_id)
    ) {
      return `${sessionId}:${message.message_id || index}:${message.content.length}`;
    }
  }
  return null;
};

export default function App() {
  const [view, setView] = useState("landing");
  const [authResolved, setAuthResolved] = useState(false);
  const [routeTransitionKey, setRouteTransitionKey] = useState(0);
  const [canvasNavigating, setCanvasNavigating] = useState(false);
  const [updatesOpen, setUpdatesOpen] = useState(() => /^\/updates\/?$/.test(window.location.pathname));
  const [docsOpen, setDocsOpen] = useState(() => /^\/docs\/?$/.test(window.location.pathname));
  const [updatesNavigating, setUpdatesNavigating] = useState(false);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(() => /^\/profile\/?$/.test(window.location.pathname));
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [themeMode, setThemeMode] = useState(() => readChatThemeMode() || readSharedTheme() || "system");
  const [isDark, setIsDark] = useState(() => {
    const mode = readChatThemeMode();
    if (mode) return mode === "system" ? Boolean(window.matchMedia?.("(prefers-color-scheme: dark)").matches) : mode === "dark";
    const sharedTheme = readSharedTheme();
    if (sharedTheme) return sharedTheme === "dark";
    const hour = new Date().getHours();
    return hour < 7 || hour >= 19;
  });
  const [user, setUser] = useState(null);
  const [tokenBalance, setTokenBalance] = useState(0);
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(() => !isMobileViewport());
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("keys");
  const [ragSelectorOpen, setRagSelectorOpen] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [animatedMessageKey, setAnimatedMessageKey] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loadingSessionId, setLoadingSessionId] = useState(null);
  const [ragCollections, setRagCollections] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [pendingSessionIds, setPendingSessionIds] = useState(() => new Set());
  const [sessionJobMap, setSessionJobMap] = useState({});
  const completedJobsRef = useRef(new Set());
  const followupSendingRef = useRef(false);
  const followupAttemptRef = useRef(null);
  const [followupSending, setFollowupSending] = useState(false);
  const [queueBusy, setQueueBusy] = useState(false);
  const [queueEditing, setQueueEditing] = useState(false);
  const queueDeliveryRef = useRef(null);
  const receiveJobRef = useRef(null);
  const [interruptingJobId, setInterruptingJobId] = useState(null);
  const [unreadSessionIds, setUnreadSessionIds] = useState(() => new Set());
  const [ttsEnabledBySession, setTtsEnabledBySession] = useState(readTtsSessionPrefs);
  const [ttsPlaying, setTtsPlaying] = useState(false);
  const [ttsPlayback, setTtsPlayback] = useState(null);
  const [newKeyData, setNewKeyData] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [authForm, setAuthForm] = useState({ username: "", password: "", confirmPassword: "", phone: "", code: "" });
  const [phoneCodeStatus, setPhoneCodeStatus] = useState(null);
  const [phoneCodeCooldown, setPhoneCodeCooldown] = useState(0);
  const [profilePrompt, setProfilePrompt] = useState({ open: false, needsUsername: false, needsPassword: false });
  const [profileForm, setProfileForm] = useState({ username: "", password: "", confirmPassword: "" });
  const [profileMessage, setProfileMessage] = useState(null);
  const [sandboxConfig, setSandboxConfig] = useState({
    baseUrl: `${API_BASE}/v1`,
    model: "",
  });
  const [availableModels, setAvailableModels] = useState([]);
  const [chatMessage, setChatMessage] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadedPath, setUploadedPath] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [webUsage, setWebUsage] = useState(null);
  const [apiUsage, setApiUsage] = useState(null);
  const [billingMessage, setBillingMessage] = useState(null);
  const [ragUploadStatus, setRagUploadStatus] = useState(null);
  const [voiceMode, setVoiceMode] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isVoiceFinalizing, setIsVoiceFinalizing] = useState(false);
  const [voiceCanceling, setVoiceCanceling] = useState(false);
  const [voiceSending, setVoiceSending] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState("");
  const fileInputRef = useRef(null);
  const ragFileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const chatScrollRef = useRef(null);
  const chatEndRef = useRef(null);
  const isComposingRef = useRef(false);
  const activeSessionIdRef = useRef(activeSessionId);
  const sessionMessageCacheRef = useRef({});
  const pollIndexRef = useRef(0);
  const ttsAudioRef = useRef(null);
  const ttsAudioUnlockedRef = useRef(false);
  const ttsPlayedJobIdsRef = useRef(new Set());
  const asrSocketRef = useRef(null);
  const audioContextRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioSourceRef = useRef(null);
  const audioProcessorRef = useRef(null);
  const audioGainRef = useRef(null);
  const voiceCommittedRef = useRef("");
  const voicePartialRef = useRef("");
  const voiceSentencesRef = useRef(new Map());
  const voiceLatestTextRef = useRef("");
  const voiceActiveRef = useRef(false);
  const voiceStopRequestedRef = useRef(false);
  const voiceWaveformRef = useRef(null);
  const voiceOverlayRef = useRef(null);
  const voiceCancelTargetRef = useRef(null);
  const voiceSendTargetRef = useRef(null);
  const voiceOverlayWaveformRef = useRef(null);
  const voiceLiveTextRef = useRef(null);
  const voiceMeterFrameRef = useRef(null);
  const voiceLevelRef = useRef(0);
  const voiceHoldStartedAtRef = useRef(null);
  const voiceShortCaptureRef = useRef(false);
  const voicePointerPositionRef = useRef(null);
  const voiceCancelingRef = useRef(false);
  const voiceSendingRef = useRef(false);
  const voiceFinalizeTimerRef = useRef(null);
  const voiceFinalizeDeadlineRef = useRef(0);

  const stopVoiceMeter = () => {
    if (voiceMeterFrameRef.current) {
      window.cancelAnimationFrame(voiceMeterFrameRef.current);
      voiceMeterFrameRef.current = null;
    }
    voiceLevelRef.current = 0;
    voiceWaveformRef.current?.style.setProperty("--voice-scale", "0.32");
    const canvas = voiceOverlayWaveformRef.current;
    if (canvas) {
      const context = canvas.getContext("2d");
      context?.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  const startVoiceMeter = () => {
    stopVoiceMeter();
    let activeCanvas = null;
    let context = null;
    let canvasWidth = 0;
    let canvasHeight = 0;
    let waveformGradient = null;
    let displayedLevel = 0;

    const prepareCanvas = () => {
      const canvas = voiceOverlayWaveformRef.current;
      if (!canvas || canvas === activeCanvas) return;
      const bounds = canvas.getBoundingClientRect();
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      canvasWidth = Math.max(1, bounds.width);
      canvasHeight = Math.max(1, bounds.height);
      canvas.width = Math.round(canvasWidth * pixelRatio);
      canvas.height = Math.round(canvasHeight * pixelRatio);
      context = canvas.getContext("2d");
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      waveformGradient = context.createLinearGradient(0, 0, canvasWidth, 0);
      waveformGradient.addColorStop(0, "rgba(156, 189, 220, 0)");
      waveformGradient.addColorStop(0.2, "rgba(156, 189, 220, 0.92)");
      waveformGradient.addColorStop(0.5, "rgba(224, 192, 137, 0.98)");
      waveformGradient.addColorStop(0.8, "rgba(156, 189, 220, 0.92)");
      waveformGradient.addColorStop(1, "rgba(156, 189, 220, 0)");
      activeCanvas = canvas;
    };

    const update = (now) => {
      const targetLevel = Math.max(0, Math.min(1, voiceLevelRef.current));
      const smoothing = targetLevel > displayedLevel ? 0.28 : 0.1;
      displayedLevel += (targetLevel - displayedLevel) * smoothing;
      prepareCanvas();
      if (context) {
        context.clearRect(0, 0, canvasWidth, canvasHeight);
        context.fillStyle = waveformGradient;
        const barCount = Math.max(30, Math.floor(canvasWidth / 9));
        const gap = canvasWidth / barCount;
        const barWidth = Math.max(1.5, Math.min(2.5, gap * 0.32));
        const phase = now * 0.009;
        const amplitude = 4 + displayedLevel * Math.min(36, canvasHeight * 0.76);
        for (let index = 0; index < barCount; index += 1) {
          const distance = Math.abs((index - (barCount - 1) / 2) / (barCount / 2));
          const envelope = 0.34 + (1 - distance) * 0.66;
          const movement = 0.3 + Math.abs(Math.sin(index * 0.72 + phase)) * 0.7;
          const height = Math.max(2, amplitude * envelope * movement);
          const x = index * gap + (gap - barWidth) / 2;
          context.globalAlpha = 0.46 + movement * 0.52;
          context.fillRect(x, (canvasHeight - height) / 2, barWidth, height);
        }
        context.globalAlpha = 1;
      }
      voiceMeterFrameRef.current = window.requestAnimationFrame(update);
    };
    voiceMeterFrameRef.current = window.requestAnimationFrame(update);
  };

  const releaseVoiceAudio = () => {
    stopVoiceMeter();
    if (audioProcessorRef.current) {
      audioProcessorRef.current.onaudioprocess = null;
      audioProcessorRef.current.disconnect();
      audioProcessorRef.current = null;
    }
    if (audioSourceRef.current) {
      audioSourceRef.current.disconnect();
      audioSourceRef.current = null;
    }
    if (audioGainRef.current) {
      audioGainRef.current.disconnect();
      audioGainRef.current = null;
    }
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current = null;
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
  };

  const clearVoiceFinalizeTimer = () => {
    if (voiceFinalizeTimerRef.current) {
      window.clearTimeout(voiceFinalizeTimerRef.current);
      voiceFinalizeTimerRef.current = null;
    }
    voiceFinalizeDeadlineRef.current = 0;
  };

  const finishVoiceFinalization = ({ emptyMessage = "" } = {}) => {
    clearVoiceFinalizeTimer();
    const text = voiceLatestTextRef.current.trim();
    setChatMessage(text);
    releaseVoiceAudio();
    voiceActiveRef.current = false;
    voiceStopRequestedRef.current = false;
    voiceShortCaptureRef.current = false;
    setIsRecording(false);
    setIsVoiceFinalizing(false);
    setVoiceStatus(text ? "" : emptyMessage);
    const socket = asrSocketRef.current;
    asrSocketRef.current = null;
    if (socket?.readyState === WebSocket.OPEN || socket?.readyState === WebSocket.CONNECTING) {
      socket.close(1000, "voice_text_settled");
    }
  };

  const scheduleVoiceFinalization = (delayMs) => {
    if (!voiceStopRequestedRef.current || voiceShortCaptureRef.current) return;
    if (!voiceFinalizeDeadlineRef.current) {
      voiceFinalizeDeadlineRef.current = performance.now() + 3000;
    }
    if (voiceFinalizeTimerRef.current) window.clearTimeout(voiceFinalizeTimerRef.current);
    const remainingMs = Math.max(0, voiceFinalizeDeadlineRef.current - performance.now());
    voiceFinalizeTimerRef.current = window.setTimeout(() => {
      finishVoiceFinalization({ emptyMessage: "没有识别到清晰内容，请再试一次" });
    }, Math.min(delayMs, remainingMs));
  };

  const stopVoiceCapture = ({ cancel = false } = {}) => {
    if (!voiceActiveRef.current && !asrSocketRef.current) return;
    const elapsed = voiceHoldStartedAtRef.current
      ? performance.now() - voiceHoldStartedAtRef.current
      : Number.POSITIVE_INFINITY;
    const isShortCapture = cancel || elapsed < 2000;
    voiceActiveRef.current = false;
    voiceStopRequestedRef.current = true;
    voiceShortCaptureRef.current = isShortCapture;
    voiceHoldStartedAtRef.current = null;
    voicePointerPositionRef.current = null;
    voiceCancelingRef.current = false;
    voiceSendingRef.current = false;
    setVoiceMode(false);
    setVoiceCanceling(false);
    setVoiceSending(false);
    setChatMessage(isShortCapture ? "" : voiceLatestTextRef.current);
    releaseVoiceAudio();
    const socket = asrSocketRef.current;
    if (isShortCapture) {
      clearVoiceFinalizeTimer();
      if (socket?.readyState === WebSocket.OPEN || socket?.readyState === WebSocket.CONNECTING) {
        if (socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ type: "stop" }));
        socket.close(1000, cancel ? "voice_cancelled" : "voice_too_short");
      }
      asrSocketRef.current = null;
      setIsRecording(false);
      setIsVoiceFinalizing(false);
      setVoiceStatus("");
      return;
    }
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: "stop" }));
      setIsRecording(false);
      setIsVoiceFinalizing(true);
      setVoiceStatus("正在整理文字...");
      voiceFinalizeDeadlineRef.current = performance.now() + 3000;
      scheduleVoiceFinalization(3000);
    } else if (socket?.readyState === WebSocket.CONNECTING) {
      setIsRecording(false);
      setIsVoiceFinalizing(true);
      setVoiceStatus("正在连接语音识别...");
      voiceFinalizeDeadlineRef.current = performance.now() + 3000;
      scheduleVoiceFinalization(3000);
    } else {
      setIsRecording(false);
      setIsVoiceFinalizing(false);
      setVoiceStatus(isShortCapture ? (cancel ? "已取消" : "录音太短，已取消") : "");
    }
  };

  const startVoiceCapture = async () => {
    if (voiceActiveRef.current || isVoiceFinalizing) return;
    clearVoiceFinalizeTimer();
    voiceActiveRef.current = true;
    voiceStopRequestedRef.current = false;
    voiceShortCaptureRef.current = false;
    voiceHoldStartedAtRef.current = performance.now();
    if (!user?.id) {
      voiceActiveRef.current = false;
      voiceHoldStartedAtRef.current = null;
      setVoiceStatus("请先登录后再使用语音输入");
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || typeof window.WebSocket === "undefined") {
      voiceActiveRef.current = false;
      voiceHoldStartedAtRef.current = null;
      setVoiceStatus("当前浏览器不支持语音输入");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      if (voiceStopRequestedRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        voiceActiveRef.current = false;
        voiceHoldStartedAtRef.current = null;
        setIsRecording(false);
        setIsVoiceFinalizing(false);
        setVoiceStatus("");
        return;
      }
      const wsUrl = API_BASE.replace(/^http/, "ws") + "/v1/audio/asr";
      const socket = new WebSocket(wsUrl);
      asrSocketRef.current = socket;
      mediaStreamRef.current = stream;
      voiceCommittedRef.current = "";
      voicePartialRef.current = "";
      voiceSentencesRef.current = new Map();
      voiceLatestTextRef.current = "";
      setChatMessage("");
      setIsRecording(true);
      setIsVoiceFinalizing(false);
      setVoiceStatus("正在连接语音识别...");
      socket.onopen = async () => {
        try {
          const AudioContextClass = window.AudioContext || window.webkitAudioContext;
          const audioContext = new AudioContextClass({ sampleRate: 16000 });
          await audioContext.resume();
          const source = audioContext.createMediaStreamSource(stream);
          const processor = audioContext.createScriptProcessor(2048, 1, 1);
          const silentGain = audioContext.createGain();
          silentGain.gain.value = 0;
          processor.onaudioprocess = (event) => {
            if (socket.readyState !== WebSocket.OPEN) return;
            const samples = event.inputBuffer.getChannelData(0);
            let energy = 0;
            for (let index = 0; index < samples.length; index += 1) {
              energy += samples[index] * samples[index];
            }
            const targetLevel = Math.min(1, Math.sqrt(energy / Math.max(1, samples.length)) * 5);
            voiceLevelRef.current += (targetLevel - voiceLevelRef.current) * 0.34;
            const pcm = downsampleToPcm16(samples, audioContext.sampleRate, 16000);
            if (pcm.byteLength > 0) socket.send(pcm);
          };
          source.connect(processor);
          processor.connect(silentGain);
          silentGain.connect(audioContext.destination);
          audioContextRef.current = audioContext;
          audioSourceRef.current = source;
          audioProcessorRef.current = processor;
          audioGainRef.current = silentGain;
          startVoiceMeter();
          if (voiceStopRequestedRef.current) {
            releaseVoiceAudio();
            socket.send(JSON.stringify({ type: "stop" }));
          } else {
            setVoiceStatus("正在聆听，松开结束");
          }
        } catch (error) {
          voiceActiveRef.current = false;
          voiceHoldStartedAtRef.current = null;
          setVoiceStatus(`无法启动麦克风：${error.message}`);
          releaseVoiceAudio();
          socket.close();
        }
      };
      socket.onmessage = (event) => {
        let payload;
        try {
          payload = JSON.parse(event.data);
        } catch {
          return;
        }
        if (payload.type === "ready") {
          if (!voiceStopRequestedRef.current) setVoiceStatus("正在聆听，松开结束");
        } else if (payload.type === "transcript") {
          if (voiceShortCaptureRef.current) return;
          let nextTranscript;
          if (payload.sentence_id !== null && payload.sentence_id !== undefined) {
            const sentenceKey = String(payload.sentence_id);
            voiceSentencesRef.current.set(sentenceKey, {
              id: Number(payload.sentence_id),
              text: payload.text,
              isFinal: Boolean(payload.is_final),
              beginTime: payload.begin_time !== null
                && payload.begin_time !== undefined
                && Number.isFinite(Number(payload.begin_time))
                ? Number(payload.begin_time)
                : Number.POSITIVE_INFINITY,
            });
            const orderedSentences = Array.from(voiceSentencesRef.current.values())
              .sort((left, right) => {
                if (left.beginTime !== right.beginTime) return left.beginTime - right.beginTime;
                return left.id - right.id;
              });
            nextTranscript = orderedSentences.map((sentence) => sentence.text).join("");
            voiceCommittedRef.current = orderedSentences
              .filter((sentence) => sentence.isFinal)
              .map((sentence) => sentence.text)
              .join("");
            voicePartialRef.current = orderedSentences
              .filter((sentence) => !sentence.isFinal)
              .map((sentence) => sentence.text)
              .join("");
          } else {
            if (payload.is_final) {
              voiceCommittedRef.current += payload.text;
              voicePartialRef.current = "";
            } else {
              voicePartialRef.current = payload.text;
            }
            nextTranscript = `${voiceCommittedRef.current}${voicePartialRef.current}`;
          }
          voiceLatestTextRef.current = nextTranscript;
          if (voiceLiveTextRef.current) {
            voiceLiveTextRef.current.textContent = nextTranscript || "请开始说话...";
            voiceLiveTextRef.current.classList.toggle("placeholder", !nextTranscript);
          }
          if (payload.is_final) setChatMessage(nextTranscript);
          if (voiceStopRequestedRef.current) {
            scheduleVoiceFinalization(payload.is_final ? 350 : 3000);
          }
        } else if (payload.type === "upstream_complete") {
          if (voiceShortCaptureRef.current) {
            clearVoiceFinalizeTimer();
            setChatMessage("");
          } else {
            finishVoiceFinalization();
          }
        } else if (payload.type === "error") {
          clearVoiceFinalizeTimer();
          releaseVoiceAudio();
          voiceActiveRef.current = false;
          voiceStopRequestedRef.current = false;
          setIsRecording(false);
          setIsVoiceFinalizing(false);
          setVoiceStatus(payload.message || "语音识别失败");
          asrSocketRef.current = null;
          socket.close();
        }
      };
      socket.onerror = () => {
        clearVoiceFinalizeTimer();
        releaseVoiceAudio();
        voiceActiveRef.current = false;
        voiceStopRequestedRef.current = false;
        voiceHoldStartedAtRef.current = null;
        setIsRecording(false);
        setIsVoiceFinalizing(false);
        setVoiceStatus("语音识别连接失败");
      };
      socket.onclose = () => {
        if (voiceStopRequestedRef.current && !voiceShortCaptureRef.current) {
          finishVoiceFinalization({ emptyMessage: "没有识别到清晰内容，请再试一次" });
          return;
        }
        clearVoiceFinalizeTimer();
        releaseVoiceAudio();
        voiceActiveRef.current = false;
        setIsRecording(false);
        setIsVoiceFinalizing(false);
        asrSocketRef.current = null;
      };
    } catch (error) {
      clearVoiceFinalizeTimer();
      releaseVoiceAudio();
      voiceActiveRef.current = false;
      voiceStopRequestedRef.current = false;
      voiceHoldStartedAtRef.current = null;
      setIsRecording(false);
      setIsVoiceFinalizing(false);
      setVoiceStatus(`无法访问麦克风：${error.message}`);
    }
  };

  const handleVoicePointerDown = (event) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    voicePointerPositionRef.current = { x: event.clientX, y: event.clientY };
    voiceCancelingRef.current = false;
    voiceSendingRef.current = true;
    setVoiceCanceling(false);
    setVoiceSending(true);
    startVoiceCapture();
  };

  const handleVoicePointerMove = (event) => {
    if (!voiceActiveRef.current || !voicePointerPositionRef.current) return;
    const point = { x: event.clientX, y: event.clientY };
    voicePointerPositionRef.current = point;
    const isInside = (element, padding = 0) => {
      if (!element) return false;
      const rect = element.getBoundingClientRect();
      return (
        point.x >= rect.left - padding
        && point.x <= rect.right + padding
        && point.y >= rect.top - padding
        && point.y <= rect.bottom + padding
      );
    };
    const nextCanceling = isInside(voiceCancelTargetRef.current, 8);
    const nextSending = !nextCanceling && isInside(voiceSendTargetRef.current, 12);
    if (nextCanceling !== voiceCancelingRef.current) {
      voiceCancelingRef.current = nextCanceling;
      setVoiceCanceling(nextCanceling);
    }
    if (nextSending !== voiceSendingRef.current) {
      voiceSendingRef.current = nextSending;
      setVoiceSending(nextSending);
    }
  };

  const handleVoicePointerUp = (event) => {
    event.preventDefault();
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    const cancel = voiceCancelingRef.current;
    voicePointerPositionRef.current = null;
    voiceCancelingRef.current = false;
    voiceSendingRef.current = false;
    setVoiceSending(false);
    stopVoiceCapture({ cancel });
  };

  const handleVoiceKeyDown = (event) => {
    if (event.key !== " " && event.key !== "Enter") return;
    event.preventDefault();
    if (!event.repeat) startVoiceCapture();
  };

  const handleVoiceKeyUp = (event) => {
    if (event.key !== " " && event.key !== "Enter") return;
    event.preventDefault();
    stopVoiceCapture();
  };

  const openVoiceMode = () => {
    if (isRecording || isVoiceFinalizing) return;
    setVoiceMode(true);
    setVoiceCanceling(false);
    setVoiceSending(false);
    setVoiceStatus("");
  };

  const closeVoiceMode = () => {
    if (isRecording || isVoiceFinalizing) return;
    setVoiceMode(false);
    setVoiceCanceling(false);
    setVoiceSending(false);
    setVoiceStatus("");
  };

  useEffect(() => () => {
    clearVoiceFinalizeTimer();
    releaseVoiceAudio();
    if (asrSocketRef.current?.readyState === WebSocket.OPEN) asrSocketRef.current.close();
    // The cleanup must be registered once so it cannot follow transient voice state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    activeSessionIdRef.current = activeSessionId;
  }, [activeSessionId]);

  useEffect(() => {
    if (phoneCodeCooldown <= 0) return undefined;
    const timer = window.setInterval(() => {
      setPhoneCodeCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [phoneCodeCooldown]);

  const closeSidebarOnMobile = () => {
    if (isMobileViewport()) {
      setSidebarOpen(false);
      return true;
    }
    return false;
  };

  const setSharedTheme = useCallback((nextIsDark) => {
    setIsDark(nextIsDark);
    writeSharedTheme(nextIsDark ? "dark" : "light");
  }, []);

  useEffect(() => {
    document.documentElement.dataset.lumaTheme = isDark ? "dark" : "light";
    document.documentElement.style.colorScheme = isDark ? "dark" : "light";
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", isDark ? "#1b1a27" : "#f2f8f6");
  }, [isDark]);

  const toggleTheme = useCallback((event, requestedDark) => {
    const nextIsDark = typeof requestedDark === "boolean" ? requestedDark : !isDark;
    if (typeof requestedDark !== "boolean") {
      const mode = nextIsDark ? "dark" : "light";
      setThemeMode(mode);
      try { window.localStorage.setItem("lumanova_chat_theme_mode",mode); } catch { /* Storage can be disabled. */ }
    }
    const applyTheme = () => setSharedTheme(nextIsDark);
    revealTheme(() => flushSync(applyTheme));
  }, [isDark, setSharedTheme]);

  const chooseThemeMode = (mode, event) => {
    setThemeMode(mode);
    try { window.localStorage.setItem("lumanova_chat_theme_mode",mode); } catch { /* Storage can be disabled. */ }
    const next = mode === "system" ? Boolean(window.matchMedia?.("(prefers-color-scheme: dark)").matches) : mode === "dark";
    if (next !== isDark) toggleTheme({ currentTarget: event.currentTarget.closest(".appearance-option") }, next);
  };

  useEffect(() => {
    if (themeMode !== "system" || typeof window.matchMedia !== "function") return undefined;
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const sync = () => setSharedTheme(media.matches);
    sync(); media.addEventListener?.("change", sync);
    return () => media.removeEventListener?.("change", sync);
  }, [themeMode, setSharedTheme]);

  const transitionToView = (nextView) => {
    setRouteTransitionKey((prev) => prev + 1);
    setView(nextView);
  };

  const setAuthView = (nextView) => {
    setPhoneCodeStatus(null);
    transitionToView(nextView);
  };

  const navigateUpdates = (open) => {
    setNoticeOpen(false);
    setDocsOpen(false);
    setUpdatesNavigating(true);
    setRouteTransitionKey((previous) => previous + 1);
    if (open) window.history.pushState({ lumaUpdates: true, returnToDocs: docsOpen }, "", "/updates");
    else if (window.history.state?.lumaUpdates) window.history.back();
    else window.history.replaceState(null, "", "/");
    setUpdatesOpen(open);
  };

  const navigateDocs = (section = "quickstart") => {
    setNoticeOpen(false);
    setAccountMenuOpen(false);
    closeSidebarOnMobile();
    setUpdatesOpen(false);
    setProfileOpen(false);
    setUpdatesNavigating(true);
    setRouteTransitionKey(previous => previous + 1);
    if (section) window.history.pushState({ lumaDocs: true }, "", `/docs#${section}`);
    else window.history.replaceState(null, "", "/");
    setDocsOpen(Boolean(section));
  };

  const navigateProfile = (open) => {
    setNoticeOpen(false);
    setAccountMenuOpen(false);
    setProfileOpen(open);
    closeSidebarOnMobile();
    if (open) window.history.pushState({lumaProfile:true},"","/profile");
    else if (window.history.state?.lumaProfile) window.history.back();
    else window.history.replaceState(null,"","/");
    setRouteTransitionKey(previous=>previous+1);
  };

  useEffect(() => {
    const handlePopState = () => {
      setNoticeOpen(false);
      setUpdatesNavigating(true);
      setUpdatesOpen(/^\/updates\/?$/.test(window.location.pathname));
      setDocsOpen(/^\/docs\/?$/.test(window.location.pathname));
      setProfileOpen(/^\/profile\/?$/.test(window.location.pathname));
      setRouteTransitionKey((previous) => previous + 1);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    if (!updatesNavigating) return undefined;
    const timer = window.setTimeout(() => setUpdatesNavigating(false), 460);
    return () => window.clearTimeout(timer);
  }, [updatesNavigating]);

  useEffect(() => {
    document.title = docsOpen ? "开发文档 | LumaNova" : updatesOpen ? "更新公告 | LumaNova" : profileOpen ? "个人主页 | LumaNova" : ICON_CONFIG.siteTitle;
  }, [updatesOpen, profileOpen, docsOpen]);

  useEffect(() => {
    if (!authResolved || !user?.id || view !== "chat" || updatesOpen || docsOpen || profileOpen || updatesNavigating || profilePrompt.open) {
      setNoticeOpen(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setNoticeOpen(!announcementMuted(user.id)), 1050);
    const syncPreference = () => { if (announcementMuted(user.id)) setNoticeOpen(false); };
    window.addEventListener("storage", syncPreference);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("storage", syncPreference);
    };
  }, [authResolved, user?.id, view, updatesOpen, docsOpen, profileOpen, updatesNavigating, profilePrompt.open]);

  useEffect(() => {
    const link = document.querySelector("link[rel*='icon']") || document.createElement("link");
    link.type = "image/png";
    link.rel = "shortcut icon";
    link.href = ICON_CONFIG.favicon;
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    writeSharedTheme(isDark ? "dark" : "light");
    // Only seed the shared preference once; subsequent changes are written by the toggle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const syncSharedTheme = () => {
      const mode = readChatThemeMode();
      if (mode) {
        setThemeMode(mode);
        setIsDark(mode === "system" ? Boolean(window.matchMedia?.("(prefers-color-scheme: dark)").matches) : mode === "dark");
        return;
      }
      const sharedTheme = readSharedTheme();
      if (sharedTheme) setIsDark(sharedTheme === "dark");
    };
    const handleVisibilityChange = () => {
      if (!document.hidden) syncSharedTheme();
    };
    window.addEventListener("focus", syncSharedTheme);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("focus", syncSharedTheme);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const restoreSession = async () => {
      try {
        window.localStorage.removeItem("lumanova_auth_v1");
        const response = await apiFetch(`${API_BASE}/auth/session`);
        if (!response.ok) {
          if (response.status === 401) clearAnnouncementPreferences();
          return;
        }
        const data = await response.json();
        if (!data?.user_id) return;
        await completeLogin(data, data.profile?.username || "", {
          restored: true,
          onAuthenticated: () => setAuthResolved(true),
        });
      } catch (error) {
        console.error(error);
      } finally {
        setAuthResolved(true);
      }
    };
    restoreSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const updateViewportHeight = () => {
      const viewport = window.visualViewport;
      const height = viewport?.height || window.innerHeight;
      document.documentElement.style.setProperty("--app-height", `${height}px`);
    };
    updateViewportHeight();
    window.addEventListener("resize", updateViewportHeight);
    window.visualViewport?.addEventListener("resize", updateViewportHeight);
    window.visualViewport?.addEventListener("scroll", updateViewportHeight);
    return () => {
      window.removeEventListener("resize", updateViewportHeight);
      window.visualViewport?.removeEventListener("resize", updateViewportHeight);
      window.visualViewport?.removeEventListener("scroll", updateViewportHeight);
    };
  }, []);

  const scrollChatToBottom = (behavior = "smooth") => {
    window.requestAnimationFrame(() => {
      chatEndRef.current?.scrollIntoView({ behavior, block: "end" });
      const scroller = chatScrollRef.current;
      if (scroller) scroller.scrollTo({ top: scroller.scrollHeight, behavior });
    });
  };

  const keepComposerVisible = () => {
    scrollChatToBottom("auto");
    [80, 220, 420, 700].forEach((delay) => {
      window.setTimeout(() => scrollChatToBottom("auto"), delay);
    });
  };

  const keepExecutionInView = () => {
    const scroller = chatScrollRef.current;
    if (scroller && scroller.scrollHeight-scroller.scrollTop-scroller.clientHeight < 220) scrollChatToBottom("auto");
  };

  const activeSessionPending = Boolean(activeSessionId && pendingSessionIds.has(activeSessionId));
  const activeSessionJob = activeSessionId ? sessionJobMap[activeSessionId] : null;
  const activeToolJob = activeSessionJob;
  const latestAssistantIndex = chatHistory.reduce((latest, message, index) => message.role === "assistant" ? index : latest, -1);
  const activeSession = sessions.find((session) => session.session_id === activeSessionId);
  const activeTtsEnabled = Boolean(activeSessionId && ttsEnabledBySession[activeSessionId]);
  const latestUserMessageIndex = chatHistory.reduce(
    (latest, message, index) => (message.role === "user" ? index : latest),
    -1
  );
  const activeChatMode = activeSession?.chat_mode || "normal";
  const activeRag = activeSession?.rag || { mode: "none", collection_id: "" };
  const canSteerTask = activeSessionPending && activeChatMode === "normal" && activeRag.mode === "none";
  const composerQueue = useComposerQueue(user?.id, activeSessionId);
  const steeringDraft = canSteerTask && Boolean(chatMessage.trim() || uploadedPath);
  const displayMessages = taskTimelineMessages(chatHistory, activeSessionPending ? activeToolJob : null);
  const activeRagCollection = ragCollections.find((collection) => collection.collection_id === activeRag.collection_id);

  useEffect(() => {
    try {
      window.localStorage.setItem(TTS_SESSION_PREFS_KEY, JSON.stringify(ttsEnabledBySession));
    } catch {
      // Local preference persistence is best-effort.
    }
  }, [ttsEnabledBySession]);

  const unlockTtsAudio = () => {
    if (ttsAudioUnlockedRef.current) return;
    const audio = ttsAudioRef.current || new Audio();
    ttsAudioRef.current = audio;
    audio.pause();
    audio.currentTime = 0;
    audio.onplay = null;
    audio.onended = null;
    audio.onerror = null;
    // Browsers reject delayed autoplay unless the media element was unlocked
    // during a user gesture. A muted one-sample WAV is enough to unlock it.
    audio.muted = true;
    audio.src = TTS_UNLOCK_AUDIO_URL;
    const unlockPromise = audio.play();
    const finishUnlock = () => {
      ttsAudioUnlockedRef.current = true;
      audio.pause();
      audio.currentTime = 0;
      audio.muted = false;
      audio.removeAttribute("src");
      audio.load();
    };
    if (unlockPromise?.then) {
      unlockPromise.then(finishUnlock).catch(() => {
        audio.muted = false;
      });
    } else {
      finishUnlock();
    }
  };

  const toggleActiveTts = () => {
    if (!activeSessionId) return;
    const enabled = !ttsEnabledBySession[activeSessionId];
    setTtsEnabledBySession((previous) => ({
      ...previous,
      [activeSessionId]: enabled,
    }));
    setTtsPlaying(false);

    if (enabled) {
      unlockTtsAudio();
    } else {
      ttsAudioUnlockedRef.current = false;
      const audio = ttsAudioRef.current;
      if (audio) {
        audio.pause();
        audio.muted = false;
        audio.removeAttribute("src");
        audio.load();
      }
    }
  };

  const openCanvas = async () => {
    if (canvasNavigating) return;
    try {
      const response = await apiFetch(`${API_BASE}/auth/canvas/access`, { method: "POST" });
      if (!response.ok) throw new Error("canvas access denied");
      setCanvasNavigating(true);
      window.setTimeout(() => {
        window.location.assign("https://canvas.lumanova.icu/?entry=luma");
      }, 900);
    } catch {
      setCanvasNavigating(false);
    }
  };
  const ragLabel = activeChatMode === "video_creation"
    ? "视频创作"
    : activeRag.mode === "all"
      ? "全部知识库"
      : activeRag.mode === "collection"
        ? activeRagCollection?.name || "知识库"
        : "普通对话";

  useEffect(() => {
    scrollChatToBottom("smooth");
  }, [chatHistory, activeSessionPending, previewUrl]);

  const addPendingSession = (sessionId) => {
    if (!sessionId) return;
    setPendingSessionIds((prev) => {
      if (prev.has(sessionId)) return prev;
      return new Set([...prev, sessionId]);
    });
  };

  const removePendingSession = (sessionId) => {
    if (!sessionId) return;
    setPendingSessionIds((prev) => {
      if (!prev.has(sessionId)) return prev;
      const next = new Set(prev);
      next.delete(sessionId);
      return next;
    });
  };

  const markSessionUnread = (sessionId) => {
    if (!sessionId || activeSessionIdRef.current === sessionId) return;
    setUnreadSessionIds((prev) => new Set([...prev, sessionId]));
  };

  const clearSessionUnread = (sessionId) => {
    if (!sessionId) return;
    setUnreadSessionIds((prev) => {
      const next = new Set(prev);
      next.delete(sessionId);
      return next;
    });
  };

  const refreshUserData = async (userId) => {
    const id = userId || user?.id;
    if (!id) return;
    try {
      const [tokenRes, keyRes] = await Promise.all([
        apiFetch(`${API_BASE}/query_token`),
        apiFetch(`${API_BASE}/query_key_list`),
      ]);
      if (tokenRes.status === 401 || keyRes.status === 401) return;
      const tokenData = await tokenRes.json();
      const keyData = await keyRes.json();
      setTokenBalance(tokenData.token_remaining || 0);
      setKeys(keyData.key_list || []);

      await fetchModels();
      await fetchWebUsage();
      await fetchApiUsage();
      await fetchRagCollections();
      const nextSessions = await fetchSessions(true);
      if (!nextSessions.length && !activeSessionId) {
        await createSession();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchModels = async () => {
    try {
      const res = await apiFetch(`${sandboxConfig.baseUrl}/models`);
      if (!res.ok) return;
      const data = await res.json();
      const nextModels = normalizeModels(data);
      const nextModelIds = nextModels.map((model) => model?.id).filter(Boolean);
      setAvailableModels(nextModels);
      setSandboxConfig((prev) => {
        if (!nextModelIds.length) {
          return { ...prev, model: "" };
        }
        const shouldUseFetchedDefault = !prev.model || !nextModelIds.includes(prev.model);
        return {
          ...prev,
          model: shouldUseFetchedDefault ? nextModelIds[0] : prev.model,
        };
      });
    } catch (error) {
      console.error(error);
    }
  };

  const fetchWebUsage = async () => {
    try {
      const res = await apiFetch(`${sandboxConfig.baseUrl}/web_usage`);
      if (!res.ok) return;
      setWebUsage(await res.json());
    } catch (error) {
      console.error(error);
    }
  };

  const fetchApiUsage = async () => {
    try {
      const res = await apiFetch(`${sandboxConfig.baseUrl}/api_usage`);
      if (!res.ok) return;
      setApiUsage(await res.json());
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if ((!accountMenuOpen && (!isSettingsOpen || activeTab !== "billing")) || !user?.id) return undefined;
    const refresh = () => { fetchWebUsage(); fetchApiUsage(); };
    refresh();
    const timer = window.setInterval(refresh, 30000);
    window.addEventListener("focus", refresh);
    return () => { window.clearInterval(timer); window.removeEventListener("focus", refresh); };
    // Fetch helpers use the current authenticated backend; their render identity is not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSettingsOpen, activeTab, user?.id, accountMenuOpen]);

  const fetchRagCollections = async () => {
    try {
      const res = await apiFetch(`${sandboxConfig.baseUrl}/rag/collections`);
      if (!res.ok) return [];
      const data = await res.json();
      const nextCollections = Array.isArray(data?.data) ? data.data : [];
      setRagCollections(nextCollections);
      return nextCollections;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const fetchSessions = async (selectFirst = false) => {
    try {
      const res = await apiFetch(`${sandboxConfig.baseUrl}/sessions`);
      if (!res.ok) return [];
      const data = await res.json();
      const nextSessions = Array.isArray(data?.data) ? data.data : [];
      setSessions(nextSessions);
      const nextPending = nextSessions
        .filter((session) => ["queued", "running"].includes(session?.job?.status))
        .map((session) => session.session_id);
      setPendingSessionIds(new Set(nextPending));
      setSessionJobMap((prev) => {
        const next = { ...prev };
        nextSessions.forEach((session) => {
          if (session?.job) next[session.session_id] = session.job;
        });
        return next;
      });
      if (selectFirst && nextSessions.length && !activeSessionId) {
        await loadSession(nextSessions[0].session_id);
      }
      return nextSessions;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const createSession = async (rag = null, chatMode = "normal") => {
    const payload = { title: "新对话" };
    if (rag) payload.rag = rag;
    if (chatMode && chatMode !== "normal") payload.chat_mode = chatMode;
    const res = await apiFetch(`${sandboxConfig.baseUrl}/sessions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) return null;
    const session = await res.json();
    setSessions((prev) => [session, ...prev.filter((item) => item.session_id !== session.session_id)]);
    setTtsEnabledBySession((prev) => ({ ...prev, [session.session_id]: false }));
      setActiveSessionId(session.session_id);
      clearSessionUnread(session.session_id);
      setChatHistory([]);
      setAnimatedMessageKey(null);
    return session;
  };

  const updateActiveSessionRag = async (rag) => {
    if (!user?.id) return;
    const sessionId = activeSessionId;
    if (!sessionId) {
      await createSession(rag);
      setRagSelectorOpen(false);
      return;
    }
    try {
      const res = await apiFetch(`${sandboxConfig.baseUrl}/sessions/${sessionId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rag }),
      });
      if (!res.ok) return;
      const session = await res.json();
      setSessions((prev) => prev.map((item) => (item.session_id === session.session_id ? session : item)));
      setRagSelectorOpen(false);
    } catch (error) {
      console.error(error);
    }
  };

  const updateActiveSessionMode = async (chatMode) => {
    if (!user?.id || activeSessionPending) return;
    const sessionId = activeSessionId;
    if (!sessionId) {
      await createSession(null, chatMode);
      setRagSelectorOpen(false);
      return;
    }
    try {
      const res = await apiFetch(`${sandboxConfig.baseUrl}/sessions/${sessionId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ chat_mode: chatMode }),
      });
      if (!res.ok) return;
      const session = await res.json();
      setSessions((prev) => prev.map((item) => (item.session_id === session.session_id ? session : item)));
      setRagSelectorOpen(false);
    } catch (error) {
      console.error(error);
    }
  };

  const pollRagJob = async (jobId, attempt = 0) => {
    if (!jobId) return;
    try {
      const res = await apiFetch(`${sandboxConfig.baseUrl}/rag/jobs/${jobId}`);
      if (!res.ok) return;
      const payload = await res.json();
      const job = payload?.data;
      if (!job) return;
      if (job.status === "completed") {
        setRagUploadStatus({ kind: "success", message: "知识库向量化完成" });
        await fetchRagCollections();
        window.setTimeout(() => setRagUploadStatus(null), 2600);
        return;
      }
      if (job.status === "failed") {
        setRagUploadStatus({ kind: "error", message: job.error || "知识库向量化失败" });
        await fetchRagCollections();
        return;
      }
      const progress = job.progress_total ? ` ${job.progress_current}/${job.progress_total}` : "";
      setRagUploadStatus({ kind: "running", message: `知识库正在向量化${progress}` });
      if (attempt < 120) {
        window.setTimeout(() => pollRagJob(jobId, attempt + 1), 1800);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const loadSession = async (sessionId, options = {}) => {
    if (!sessionId) return [];
    activeSessionIdRef.current = sessionId;
    setActiveSessionId(sessionId);
    clearSessionUnread(sessionId);
    setUploadedPath(null);
    setPreviewUrl(null);
    setAnimatedMessageKey(null);

    const cachedMessages = sessionMessageCacheRef.current[sessionId];
    const knownMessageIds = new Set(
      (cachedMessages || []).map((message) => message.message_id).filter(Boolean)
    );
    if (cachedMessages) {
      setChatHistory(cachedMessages);
    } else {
      setChatHistory([]);
    }
    setLoadingSessionId(sessionId);

    try {
      const res = await apiFetch(`${sandboxConfig.baseUrl}/sessions/${sessionId}/messages`);
      if (!res.ok) return [];
      const data = await res.json();
      const messages = (Array.isArray(data?.data) ? data.data : []).map((message) => ({
        message_id: message.message_id,
        role: message.role,
        content: message.content,
        message_type: message.message_type || "message",
        sequence_no: message.sequence_no,
        created_at: message.created_at,
        execution: message.execution,
      }));
      sessionMessageCacheRef.current[sessionId] = messages;
      if (activeSessionIdRef.current === sessionId) {
        setChatHistory(messages);
        setAnimatedMessageKey(
          options.animateLatestAssistant
            ? latestNewAssistantAnimationKey(sessionId, messages, knownMessageIds)
            : null
        );
        setLoadingSessionId(null);
      }
      return messages;
    } catch (error) {
      console.error(error);
      return [];
    } finally {
      if (activeSessionIdRef.current === sessionId) {
        setLoadingSessionId(null);
      }
    }
  };

  const playTtsForJob = (sessionId, job) => {
    if (!sessionId || activeSessionIdRef.current !== sessionId || !ttsEnabledBySession[sessionId]) return Promise.resolve();
    if (!job?.job_id || ttsPlayedJobIdsRef.current.has(job.job_id)) return Promise.resolve();
    const ttsUrl = job?.usage?.tts_url;
    if (!ttsUrl) return Promise.resolve();

    ttsPlayedJobIdsRef.current.add(job.job_id);
    const audio = ttsAudioRef.current || new Audio();
    ttsAudioRef.current = audio;
    audio.pause();
    audio.src = ttsUrl;
    audio.currentTime = 0;
    setTtsPlayback(null);

    return new Promise((resolve) => {
      let started = false;
      const startPlayback = () => {
        if (started) return;
        started = true;
        const durationMs = Number.isFinite(audio.duration) && audio.duration > 0
          ? audio.duration * 1000
          : 5000;
        setTtsPlayback({ jobId: job.job_id, durationMs });
        audio.play().catch(() => setTtsPlaying(false));
        resolve();
      };
      audio.onloadedmetadata = startPlayback;
      audio.onplay = () => setTtsPlaying(true);
      audio.onended = () => setTtsPlaying(false);
      audio.onerror = () => {
        setTtsPlaying(false);
        setTtsPlayback(null);
        resolve();
      };
      audio.load();
      if (audio.readyState >= 1 && Number.isFinite(audio.duration)) {
        startPlayback();
      }
      window.setTimeout(startPlayback, 2500);
    });
  };

  const receiveSessionJob = async (sessionId, job) => {
      if (!job) return;
      setSessionJobMap(prev => {
        const previous = prev[sessionId];
        if (previous?.job_id === job.job_id && Number(previous.event_seq || 0) > Number(job.event_seq || 0)) return prev;
        return { ...prev, [sessionId]: job };
      });
      if (completedJobsRef.current.has(job.job_id)) return;
      if (["queued", "running"].includes(job.status)) {
        addPendingSession(sessionId);
        return;
      }
      completedJobsRef.current.add(job.job_id);
      if (completedJobsRef.current.size > 100) completedJobsRef.current.delete(completedJobsRef.current.values().next().value);
      removePendingSession(sessionId);
      if (activeSessionIdRef.current === sessionId) {
        if (job.status === "completed") {
          await playTtsForJob(sessionId, job);
          await loadSession(sessionId, { animateLatestAssistant: true });
        } else {
          await loadSession(sessionId);
        }
      } else {
        markSessionUnread(sessionId);
      }
      await fetchSessions();
      window.setTimeout(() => {
        fetchWebUsage();
        fetchApiUsage();
      }, 1200);
  };
  receiveJobRef.current = receiveSessionJob;

  const refreshSessionJob = async (sessionId) => {
    if (!sessionId) return;
    try {
      const res = await apiFetch(`${sandboxConfig.baseUrl}/sessions/${sessionId}/job`);
      if (!res.ok) return;
      const payload = await res.json();
      await receiveJobRef.current(sessionId, payload?.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const jobId = activeSessionJob?.job_id;
    if (!activeSessionPending || !jobId || typeof EventSource === "undefined") return undefined;
    const sessionId = activeSessionId;
    const source = new EventSource(`${sandboxConfig.baseUrl}/chat/jobs/${jobId}/events`, { withCredentials: true });
    const receive = event => {
      try {
        const job = JSON.parse(event.data)?.data;
        if (job?.job_id !== jobId || (job?.session_id && job.session_id !== sessionId)) return;
        receiveJobRef.current(sessionId, job);
        if (["completed", "failed", "canceled"].includes(job.status)) source.close();
      } catch { /* Polling remains available when an event is malformed. */ }
    };
    source.addEventListener("job", receive);
    return () => { source.removeEventListener("job", receive); source.close(); };
  }, [activeSessionPending, activeSessionJob?.job_id, activeSessionId, sandboxConfig.baseUrl]);

  useEffect(() => {
    if (!user?.id || pendingSessionIds.size === 0) return undefined;
    const pollBackgroundSession = () => {
      const sessionIds = [...pendingSessionIds].filter((sessionId) => sessionId !== activeSessionIdRef.current);
      if (!sessionIds.length) return;
      pollIndexRef.current = pollIndexRef.current % sessionIds.length;
      const sessionId = sessionIds[pollIndexRef.current];
      pollIndexRef.current += 1;
      refreshSessionJob(sessionId);
    };
    const pollActiveSession = () => {
      if (!activeSessionIdRef.current || !pendingSessionIds.has(activeSessionIdRef.current)) return;
      refreshSessionJob(activeSessionIdRef.current);
    };
    const activeInitialTimer = window.setTimeout(pollActiveSession, 900);
    const activeTimer = window.setInterval(pollActiveSession, 2500);
    const backgroundTimer = window.setInterval(pollBackgroundSession, 7000);
    return () => {
      window.clearTimeout(activeInitialTimer);
      window.clearInterval(activeTimer);
      window.clearInterval(backgroundTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, sandboxConfig.baseUrl, pendingSessionIds, activeSessionId]);

  const deleteSession = async (sessionId) => {
    if (!sessionId || !user?.id) return;
    try {
      await apiFetch(`${sandboxConfig.baseUrl}/sessions/${sessionId}`, {
        method: "DELETE",
      });
      const nextSessions = sessions.filter((session) => session.session_id !== sessionId);
      setSessions(nextSessions);
      setTtsEnabledBySession((prev) => {
        if (!(sessionId in prev)) return prev;
        const next = { ...prev };
        delete next[sessionId];
        return next;
      });
      if (activeSessionId === sessionId) {
        if (nextSessions[0]) {
          await loadSession(nextSessions[0].session_id);
        } else {
          setActiveSessionId(null);
          setChatHistory([]);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const completeLogin = async (data, fallbackUsername, options = {}) => {
    if (!options.restored) clearAnnouncementPreferences();
    const profile = data.profile || {};
    const needsUsername = Boolean(profile.needs_username);
    const needsPassword = Boolean(data.needs_password || profile.needs_password);
    const displayName = profile.username || fallbackUsername;

    const nextUser = { id: data.user_id, username: displayName, phone: profile.phone || "" };
    setUser(nextUser);
    setProfileForm({
      username: needsUsername ? "" : displayName || "",
      password: "",
      confirmPassword: "",
    });
    setProfileMessage(null);
    setProfilePrompt({ open: needsUsername || needsPassword, needsUsername, needsPassword });
    if (options.animateTransition === false) {
      setView("chat");
    } else {
      transitionToView("chat");
    }
    options.onAuthenticated?.();
    await refreshUserData(data.user_id);
  };

  const handleAuth = async (type) => {
    setPhoneCodeStatus(null);
    if (type === "register") {
      if (!authForm.username.trim()) {
        setPhoneCodeStatus({ success: false, msg: "请输入用户名" });
        return;
      }
      if (!authForm.password || authForm.password !== authForm.confirmPassword) {
        setPhoneCodeStatus({ success: false, msg: "两次输入的密码不一致" });
        return;
      }
      transitionToView("bind-phone");
      return;
    }
    if (!authForm.username.trim()) {
      setPhoneCodeStatus({ success: false, msg: "请输入用户名或手机号" });
      return;
    }
    if (!authForm.password) {
      setPhoneCodeStatus({ success: false, msg: "请输入密码" });
      return;
    }
    setLoading(true);
    try {
      const endpoint = type === "login" ? "/login" : "/register_users";
      const res = await apiFetch(`${API_BASE}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(authForm),
      });
      const data = await res.json().catch(() => ({}));
      if (data.user_id) {
        await completeLogin(data, authForm.username);
      } else {
        setPhoneCodeStatus({ success: false, msg: extractResponseMessage(data, "登录失败，请检查账号或密码") });
      }
    } catch (error) {
      setPhoneCodeStatus({ success: false, msg: `登录请求异常：${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const handleSendPhoneCode = async () => {
    setPhoneCodeStatus(null);
    if (phoneCodeCooldown > 0) {
      setPhoneCodeStatus({ success: false, msg: `请 ${phoneCodeCooldown} 秒后再获取验证码` });
      return;
    }
    const phone = authForm.phone.trim();
    if (!PHONE_RE.test(phone)) {
      setPhoneCodeStatus({ success: false, msg: "请输入 11 位有效手机号" });
      return;
    }
    setLoading(true);
    try {
      const res = await apiFetch(`${API_BASE}/send_phone_code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });
      const data = await res.json().catch(() => ({}));
      setPhoneCodeStatus({ ...data, success: Boolean(data.success), msg: extractResponseMessage(data, data.success ? "验证码已发送" : "验证码发送失败") });
      if (data.success) {
        setPhoneCodeCooldown(60);
      } else if (Number(data.wait_seconds) > 0) {
        setPhoneCodeCooldown(Math.ceil(Number(data.wait_seconds)));
      }
      if (data.debug_code) {
        setAuthForm((prev) => ({ ...prev, code: data.debug_code }));
      }
    } catch (error) {
      setPhoneCodeStatus({ success: false, msg: `验证码发送异常：${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const handleSendPasswordResetCode = async () => {
    setPhoneCodeStatus(null);
    if (phoneCodeCooldown > 0) {
      setPhoneCodeStatus({ success: false, msg: `请 ${phoneCodeCooldown} 秒后再获取验证码` });
      return;
    }
    const phone = authForm.phone.trim();
    if (!PHONE_RE.test(phone)) {
      setPhoneCodeStatus({ success: false, msg: "请输入 11 位有效手机号" });
      return;
    }
    setLoading(true);
    try {
      const res = await apiFetch(`${API_BASE}/auth/password/reset/sms/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });
      const data = await res.json().catch(() => ({}));
      setPhoneCodeStatus({
        ...data,
        success: Boolean(data.success),
        msg: extractResponseMessage(data, data.success ? "验证码已发送" : "验证码发送失败"),
      });
      if (data.success) {
        setPhoneCodeCooldown(60);
      } else if (Number(data.wait_seconds) > 0) {
        setPhoneCodeCooldown(Math.ceil(Number(data.wait_seconds)));
      }
      if (data.debug_code) {
        setAuthForm((prev) => ({ ...prev, code: data.debug_code }));
      }
    } catch (error) {
      setPhoneCodeStatus({ success: false, msg: `验证码发送异常：${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    setPhoneCodeStatus(null);
    const phone = authForm.phone.trim();
    if (!PHONE_RE.test(phone)) {
      setPhoneCodeStatus({ success: false, msg: "请输入 11 位有效手机号" });
      return;
    }
    if (!authForm.code.trim()) {
      setPhoneCodeStatus({ success: false, msg: "请输入验证码" });
      return;
    }
    if (authForm.password.length < 6) {
      setPhoneCodeStatus({ success: false, msg: "新密码至少需要 6 位" });
      return;
    }
    if (authForm.password !== authForm.confirmPassword) {
      setPhoneCodeStatus({ success: false, msg: "两次输入的新密码不一致" });
      return;
    }
    setLoading(true);
    try {
      const res = await apiFetch(`${API_BASE}/auth/password/reset/sms/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone,
          code: authForm.code.trim(),
          new_password: authForm.password,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!data.success) {
        setPhoneCodeStatus({ success: false, msg: extractResponseMessage(data, "密码重置失败") });
        return;
      }
      setAuthForm((prev) => ({ ...prev, password: "", confirmPassword: "", code: "" }));
      transitionToView("login");
      setPhoneCodeStatus({ success: true, msg: data.msg || "密码已重置，请使用新密码登录" });
    } catch (error) {
      setPhoneCodeStatus({ success: false, msg: `密码重置异常：${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteRegister = async () => {
    if (!authForm.username.trim()) {
      setPhoneCodeStatus({ success: false, msg: "请输入用户名" });
      return;
    }
    if (!authForm.password || authForm.password !== authForm.confirmPassword) {
      setPhoneCodeStatus({ success: false, msg: "两次输入的密码不一致" });
      return;
    }
    const phone = authForm.phone.trim();
    if (!PHONE_RE.test(phone)) {
      setPhoneCodeStatus({ success: false, msg: "请输入 11 位有效手机号" });
      return;
    }
    if (!authForm.code.trim()) {
      setPhoneCodeStatus({ success: false, msg: "请输入验证码" });
      return;
    }
    setLoading(true);
    try {
      const res = await apiFetch(`${API_BASE}/register_phone`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: authForm.username,
          password: authForm.password,
          phone,
          code: authForm.code,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.user_id) {
        await completeLogin(data, authForm.username);
      } else {
        setPhoneCodeStatus({ success: false, msg: extractResponseMessage(data, "注册失败，请检查验证码或账号信息") });
      }
    } catch (error) {
      setPhoneCodeStatus({ success: false, msg: `注册请求异常：${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneCodeLogin = async () => {
    setPhoneCodeStatus(null);
    const phone = authForm.phone.trim();
    if (!PHONE_RE.test(phone)) {
      setPhoneCodeStatus({ success: false, msg: "请输入 11 位有效手机号" });
      return;
    }
    if (!authForm.code.trim()) {
      setPhoneCodeStatus({ success: false, msg: "请输入验证码" });
      return;
    }
    setLoading(true);
    try {
      const res = await apiFetch(`${API_BASE}/login_phone`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone, code: authForm.code }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.user_id) {
        await completeLogin(data, phone);
      } else {
        setPhoneCodeStatus({ success: false, msg: extractResponseMessage(data, "登录失败，请检查手机号或验证码") });
      }
    } catch (error) {
      setPhoneCodeStatus({ success: false, msg: `验证码登录异常：${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setProfileMessage(null);
    if (profilePrompt.needsUsername && !profileForm.username.trim()) {
      setProfileMessage({ success: false, msg: "请输入用户名" });
      return;
    }
    if (profilePrompt.needsPassword && (!profileForm.password || profileForm.password !== profileForm.confirmPassword)) {
      setProfileMessage({ success: false, msg: "两次输入的密码不一致" });
      return;
    }
    setLoading(true);
    try {
      if (profilePrompt.needsUsername) {
        const usernameRes = await apiFetch(`${API_BASE}/set_username`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ username: profileForm.username.trim() }),
        });
        const usernameData = await usernameRes.json();
        if (!usernameData.success) {
          setProfileMessage({ success: false, msg: usernameData.msg || "用户名保存失败" });
          return;
        }
        const nextUser = { ...user, username: usernameData.profile?.username || profileForm.username.trim() };
        setUser(nextUser);
      }

      if (profilePrompt.needsPassword) {
        const passwordRes = await apiFetch(`${API_BASE}/set_password`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ password: profileForm.password }),
        });
        const passwordData = await passwordRes.json();
        if (!passwordData.success) {
          setProfileMessage({ success: false, msg: passwordData.msg || "密码保存失败" });
          return;
        }
      }

      setProfilePrompt({ open: false, needsUsername: false, needsPassword: false });
      setProfileForm({ username: "", password: "", confirmPassword: "" });
      await refreshUserData(user.id);
    } finally {
      setLoading(false);
    }
  };

  const handleNewChat = async () => {
    if (user?.id) {
      await createSession();
      closeSidebarOnMobile();
      return;
    }
    setChatHistory([]);
    setChatMessage("");
    setUploadedPath(null);
    setPreviewUrl(null);
  };

  const handleLogout = async () => {
    clearAnnouncementPreferences();
    setNoticeOpen(false);
    try {
      await apiFetch(`${API_BASE}/logout`, { method: "POST" });
    } catch (error) {
      console.error(error);
    }
    setUser(null);
    setChatHistory([]);
    setSessions([]);
    setRagCollections([]);
    setRagUploadStatus(null);
    setActiveSessionId(null);
    setPendingSessionIds(new Set());
    setSessionJobMap({});
    setInterruptingJobId(null);
    setUnreadSessionIds(new Set());
    transitionToView("landing");
  };

  const responseToContent = (textResponse) => {
    try {
      const jsonData = JSON.parse(textResponse);
      if (jsonData.choices?.[0]?.message?.content) return jsonData.choices[0].message.content;
      if (jsonData.detail) return `请求失败：${typeof jsonData.detail === "string" ? jsonData.detail : JSON.stringify(jsonData.detail)}`;
      if (jsonData.error?.message) return jsonData.error.message;
      return `\`\`\`json\n${JSON.stringify(jsonData, null, 2)}\n\`\`\``;
    } catch {
      return textResponse.replace(/^"|"$/g, "");
    }
  };

  const responseErrorMessage = async (response, fallback) => {
    try {
      const payload = await response.json();
      return payload?.detail || payload?.error?.message || fallback;
    } catch {
      return fallback;
    }
  };

  const queueJobRefreshes = (sessionId) => {
    [700, 1600, 2800].forEach((delay) => {
      window.setTimeout(() => refreshSessionJob(sessionId), delay);
    });
  };

  const replaceSessionMessages = (sessionId, updater) => {
    setChatHistory((current) => {
      if (activeSessionIdRef.current !== sessionId) return current;
      const next = updater(current);
      sessionMessageCacheRef.current[sessionId] = next;
      return next;
    });
  };

  const handleInterrupt = async () => {
    const sessionId = activeSessionIdRef.current;
    const job = sessionId ? sessionJobMap[sessionId] : null;
    if (!sessionId || !job?.job_id || interruptingJobId) return;

    setInterruptingJobId(job.job_id);
    setAnimatedMessageKey(null);
    setTtsPlayback(null);
    const audio = ttsAudioRef.current;
    if (audio) {
      audio.pause();
      setTtsPlaying(false);
    }

    try {
      const response = await apiFetch(`${sandboxConfig.baseUrl}/chat/jobs/${job.job_id}/cancel`, {
        method: "POST",
      });
      if (!response.ok) {
        throw new Error(await responseErrorMessage(response, "暂时无法停止此回复"));
      }
      const payload = await response.json();
      const canceledJob = payload?.data || { ...job, status: "canceled" };
      setSessionJobMap((previous) => ({ ...previous, [sessionId]: canceledJob }));
      removePendingSession(sessionId);
      const interruption = payload?.interruption_message;
      if (interruption) {
        replaceSessionMessages(sessionId, (current) => {
          if (current.some((message) => message.message_id === interruption.message_id)) {
            return current;
          }
          return [
            ...current,
            {
              message_id: interruption.message_id,
              role: interruption.role,
              content: interruption.content,
              message_type: interruption.message_type || "interrupted",
              sequence_no: interruption.sequence_no,
              created_at: interruption.created_at,
            },
          ];
        });
      }
      await fetchSessions();
    } catch (error) {
      await refreshSessionJob(sessionId);
      console.error(error);
    } finally {
      setInterruptingJobId(null);
    }
  };

  const regenerateMessage = async (message, editedText) => {
    const sessionId = activeSessionIdRef.current;
    if (!sessionId || !message?.message_id) {
      throw new Error("这条消息尚未保存，请稍后重试");
    }
    if (pendingSessionIds.has(sessionId)) {
      throw new Error("请先停止当前回复");
    }

    const messageIndex = chatHistory.findIndex((item) => item.message_id === message.message_id);
    if (messageIndex < 0) throw new Error("没有找到要重新生成的消息");

    const isEdit = typeof editedText === "string";
    const mediaRefs = getMediaUrls(message.content || "");
    const nextContent = isEdit
      ? [editedText.trim(), ...mediaRefs].filter(Boolean).join("\n")
      : message.content;
    const optimisticMessages = chatHistory.slice(0, messageIndex + 1);
    optimisticMessages[messageIndex] = { ...message, content: nextContent };
    sessionMessageCacheRef.current[sessionId] = optimisticMessages;
    setChatHistory(optimisticMessages);
    setAnimatedMessageKey(null);
    setTtsPlayback(null);
    addPendingSession(sessionId);
    clearSessionUnread(sessionId);

    try {
      const response = await apiFetch(
        `${sandboxConfig.baseUrl}/sessions/${sessionId}/messages/${message.message_id}/regenerate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            content: isEdit ? nextContent : null,
            model: sandboxConfig.model,
            tts_enabled: Boolean(ttsEnabledBySession[sessionId]),
          }),
        }
      );
      if (response.status !== 202) {
        throw new Error(await responseErrorMessage(response, "暂时无法重新生成回复"));
      }
      const jobPayload = await response.json();
      const storedMessage = jobPayload?.user_message;
      if (storedMessage) {
        replaceSessionMessages(sessionId, (current) =>
          current.map((item) => (
            item.message_id === message.message_id
              ? {
                  message_id: storedMessage.message_id,
                  role: storedMessage.role,
                  content: storedMessage.content,
                  message_type: storedMessage.message_type || "message",
                  sequence_no: storedMessage.sequence_no,
                  created_at: storedMessage.created_at,
                }
              : item
          ))
        );
      }
      setSessionJobMap((previous) => ({ ...previous, [sessionId]: jobPayload }));
      queueJobRefreshes(sessionId);
      await fetchSessions();
      return true;
    } catch (error) {
      removePendingSession(sessionId);
      await loadSession(sessionId);
      throw error;
    }
  };

  const enqueueComposerDraft = async (text) => {
    if (!text && !uploadedPath) return;
    try {
      await composerQueue.mutate(previous => {
        if (previous.items.length >= 20) throw Error("最多可排队 20 条消息");
        if (text.length > 8000) throw Error("排队消息最多 8000 字");
        return { ...previous, items: [...previous.items, { id: window.crypto.randomUUID(), content: text || "请查看这张图片",
          image_path: uploadedPath || "", model: sandboxConfig.model, tts_enabled: activeTtsEnabled, state: "pending" }] };
      });
      setChatMessage(""); setUploadedPath(null); setPreviewUrl(null); setVoiceStatus("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (textareaRef.current) textareaRef.current.style.height = "auto";
      keepComposerVisible();
    } catch (error) { setVoiceStatus(error.message); }
  };

  const deliverQueuedMessage = async (item, requestedMode = "send") => {
    const key = composerQueue.key;
    const sessionId = activeSessionId;
    if (!key || queueBusy) return;
    setQueueBusy(true);
    try {
      await withQueueLock(`${key}:dispatch`, async () => {
        const stored = readQueue(key).items.find(value => value.id === item.id);
        if (!stored) return;
        const mode = stored.attempt || requestedMode;
        const jobId = stored.guide_job_id || activeSessionJob?.job_id;
        if (mode === "guide" && stored.image_path) throw Error("带参考图的消息请等待下一轮发送，暂不支持中途引导");
        if (mode === "guide" && !jobId) throw Error("当前没有可以引导的任务");
        await composerQueue.mutate(previous => ({ ...previous, items: previous.items.map(value => value.id === stored.id
          ? { ...value, state: "uncertain", attempt: mode, guide_job_id: mode === "guide" ? jobId : null } : value) }));
        const content = [stored.content, stored.image_path].filter(Boolean).join("\n");
        const response = await apiFetch(mode === "guide" ? `${sandboxConfig.baseUrl}/chat/jobs/${jobId}/inputs` : `${sandboxConfig.baseUrl}/chat/completions`, {
          method: "POST", headers: { "Content-Type": "application/json", "X-LumaNova-Client": "web" },
          body: JSON.stringify(mode === "guide" ? { content: stored.content, input_id: stored.id } : {
            session_id: sessionId, client_request_id: stored.id, model: stored.model,
            messages: [{ role: "user", content }], image_path: stored.image_path || "", tts_enabled: Boolean(stored.tts_enabled),
          }),
        });
        if (!response.ok) {
          const message = await responseErrorMessage(response, "消息发送失败");
          await composerQueue.mutate(previous => ({ ...previous, items: previous.items.map(value => value.id === stored.id
            ? { ...value, state: response.status === 409 ? "pending" : "blocked", attempt: null, guide_job_id: null } : value) }));
          if (response.status === 409) await refreshSessionJob(sessionId);
          if (response.status !== 409 || requestedMode === "guide") throw Error(message);
          return;
        }
        const payload = await response.json();
        if (mode === "send" && (response.status !== 202 || payload.job_id !== queuedJobId(stored.id))) {
          throw Error("发送状态待确认，请保留这条消息并重试确认");
        }
        await composerQueue.mutate(previous => ({ ...previous, items: previous.items.filter(value => value.id !== stored.id) }));
        if (mode === "send") await receiveJobRef.current?.(sessionId, payload);
        await loadSession(sessionId);
        await fetchSessions();
        setVoiceStatus("");
      });
    } catch (error) { setVoiceStatus(error.message); }
    finally { setQueueBusy(false); }
  };
  queueDeliveryRef.current = deliverQueuedMessage;
  const updateComposerQueue = async fn => {
    try { return await composerQueue.mutate(fn); }
    catch (error) { setVoiceStatus(error.message); return false; }
  };

  useEffect(() => {
    if (!composerQueue.key || !composerQueue.enabled || activeSessionPending || !activeSession
      || loadingSessionId === activeSessionId || composerQueue.items[0]?.state !== "pending" || queueBusy || queueEditing) return undefined;
    const send = () => queueDeliveryRef.current?.(composerQueue.items[0], "send");
    const timer = window.setTimeout(send, 800);
    return () => window.clearTimeout(timer);
  }, [composerQueue.key, composerQueue.enabled, composerQueue.items, activeSessionPending, activeSessionId, activeSession, loadingSessionId, queueBusy, queueEditing]);

  const handleChat = async () => {
    const text = chatMessage.trim();
    if (!text && !uploadedPath) return;
    if ((canSteerTask && composerQueue.enabled) || (!activeSessionPending && composerQueue.items.length)) {
      await enqueueComposerDraft(text);
      return;
    }
    if (activeSessionPending) {
      if (!canSteerTask) { setVoiceStatus("当前模式暂不支持执行中追加指令"); return; }
      if (uploadedPath) { setVoiceStatus("执行中暂只支持追加文字；参考图请在下一轮发送"); return; }
      if (!activeSessionJob?.job_id || followupSendingRef.current) return;
      const sessionId = activeSessionId;
      if (followupAttemptRef.current?.jobId !== activeSessionJob.job_id || followupAttemptRef.current?.text !== text) {
        followupAttemptRef.current = { jobId: activeSessionJob.job_id, text, inputId: window.crypto.randomUUID() };
      }
      followupSendingRef.current = true;
      setFollowupSending(true);
      try {
        const response = await apiFetch(`${sandboxConfig.baseUrl}/chat/jobs/${activeSessionJob.job_id}/inputs`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ content: text, input_id: followupAttemptRef.current.inputId }),
        });
        if (!response.ok) throw new Error(await responseErrorMessage(response, "追加指令暂未送达，请重试"));
        setChatMessage(current => current.trim() === text ? "" : current);
        followupAttemptRef.current = null;
        setVoiceStatus("已追加，将在当前执行的安全节点接入");
        await loadSession(sessionId);
      } catch (error) {
        setVoiceStatus(error.message);
      } finally {
        followupSendingRef.current = false;
        setFollowupSending(false);
      }
      return;
    }
    if (!user?.id) {
      setChatHistory((prev) => [...prev, { role: "assistant", content: "登录状态已失效，请重新登录。" }]);
      return;
    }
    if (!sandboxConfig.model) {
      setChatHistory((prev) => [...prev, { role: "assistant", content: "模型列表还没有加载完成，请稍后再试。" }]);
      fetchModels();
      return;
    }
    let sessionId = activeSessionId;
    if (!sessionId) {
      const session = await createSession();
      sessionId = session?.session_id;
    }
    if (!sessionId) {
      setChatHistory((prev) => [...prev, { role: "assistant", content: "会话创建失败，请稍后再试。" }]);
      return;
    }
    const requestTtsEnabled = Boolean(ttsEnabledBySession[sessionId]);
    if (requestTtsEnabled) unlockTtsAudio();

    const currentPath = uploadedPath;
    const userMsg = {
      message_id: `temp_${Date.now()}`,
      role: "user",
      content: currentPath ? (text ? `${text}\n${currentPath}` : currentPath) : text,
      message_type: "message",
    };

    addPendingSession(sessionId);
    clearSessionUnread(sessionId);
    setChatHistory((prev) => [...prev, userMsg]);
    setChatMessage("");
    setTtsPlayback(null);
    setVoiceMode(false);
    setVoiceStatus("");
    setUploadedPath(null);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (textareaRef.current) textareaRef.current.style.height = "auto";
    scrollChatToBottom("smooth");

    try {
      const res = await apiFetch(`${sandboxConfig.baseUrl}/chat/completions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-LumaNova-Client": "web",
        },
        body: JSON.stringify({
          model: sandboxConfig.model,
          messages: [userMsg],
          image_path: currentPath || "",
          session_id: sessionId,
          tts_enabled: requestTtsEnabled,
        }),
      });

      const textResponse = await res.text();
      if (res.status === 202) {
        const jobPayload = JSON.parse(textResponse);
        if (jobPayload?.session_id) {
          if (jobPayload.user_message) {
            const storedMessage = jobPayload.user_message;
            replaceSessionMessages(jobPayload.session_id, (current) =>
              current.map((item) => (
                item.message_id === userMsg.message_id
                  ? {
                      message_id: storedMessage.message_id,
                      role: storedMessage.role,
                      content: storedMessage.content,
                      message_type: storedMessage.message_type || "message",
                      sequence_no: storedMessage.sequence_no,
                      created_at: storedMessage.created_at,
                    }
                  : item
              ))
            );
          }
          addPendingSession(jobPayload.session_id);
          setSessionJobMap((prev) => ({ ...prev, [jobPayload.session_id]: jobPayload }));
          queueJobRefreshes(jobPayload.session_id);
        }
        await fetchSessions();
        return;
      }
      removePendingSession(sessionId);
      setChatHistory((prev) => [...prev, { role: "assistant", content: responseToContent(textResponse) }]);
      fetchSessions();
      refreshUserData();
      fetchWebUsage();
      fetchApiUsage();
    } catch (error) {
      removePendingSession(sessionId);
      setChatHistory((prev) => [...prev, { role: "assistant", content: `请求异常：${error.message}` }]);
    } finally {
      scrollChatToBottom("smooth");
    }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setPreviewUrl(URL.createObjectURL(file));
    setIsUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await apiFetch(`${API_BASE}/upload_image`, { method: "POST", body: fd });
      const data = await res.json();
      if (data.image_path) setUploadedPath(data.image_path);
    } finally {
      setIsUploading(false);
    }
  };

  const uploadKnowledgeFile = async (file) => {
    if (!file || !user?.id) return;
    setIsUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("collection_name", file.name.replace(/\.[^.]+$/, "") || "新知识库");
      const res = await apiFetch(`${sandboxConfig.baseUrl}/rag/files`, {
        method: "POST",
        body: fd,
      });
      const data = await res.json();
      const collection = data?.collection;
      const job = data?.job;
      if (collection?.collection_id) {
        setRagCollections((prev) => [{ ...collection, status: "indexing" }, ...prev.filter((item) => item.collection_id !== collection.collection_id)]);
        await updateActiveSessionRag({ mode: "collection", collection_id: collection.collection_id });
        setRagUploadStatus({ kind: "running", message: `知识库「${collection.name || file.name}」正在向量化` });
        pollRagJob(job?.job_id);
      }
    } catch (error) {
      setRagUploadStatus({ kind: "error", message: `知识库上传失败：${error.message}` });
    } finally {
      setIsUploading(false);
      if (ragFileInputRef.current) ragFileInputRef.current.value = "";
    }
  };

  const createKey = async (name) => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API_BASE}/register_key`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
      const data = await res.json();
      if (!res.ok || !data.success || !data.key) return { success:false, msg:data.detail || data.msg || "创建失败，请重试。" };
      setNewKeyData(data.key);
      await refreshUserData();
      return { success:true };
    } catch {
      return { success:false, msg:"网络异常，暂时无法确认创建结果，请刷新密钥列表后检查。" };
    } finally {
      setLoading(false);
    }
  };

  const deleteKey = async (keyId) => {
    try {
      const response = await apiFetch(`${API_BASE}/v1/api-keys/${encodeURIComponent(keyId)}`, { method: "DELETE" });
      const result = await response.json();
      if (!response.ok || !result.success) return { success: false, msg: result.detail || "删除失败，请稍后重试。" };
      setKeys((previous) => previous.filter((key) => key[4] !== keyId));
      return { success: true };
    } catch {
      return { success: false, msg: "网络异常，暂时无法确认删除结果，请刷新密钥列表后检查。" };
    }
  };

  const changePassword = async ({ oldPassword, newPassword }) => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API_BASE}/auth/password/change`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          old_password: oldPassword,
          new_password: newPassword,
        }),
      });
      const data = await res.json().catch(() => ({}));
      return {
        ...data,
        success: Boolean(data.success),
        msg: extractResponseMessage(data, data.success ? "密码修改成功" : "密码修改失败"),
      };
    } catch (error) {
      return { success: false, msg: `密码修改异常：${error.message}` };
    } finally {
      setLoading(false);
    }
  };

  const createCheckout = async (plan) => {
    if (!user?.id) return;
    const checkoutWindow = window.open("", "_blank");
    setLoading(true);
    setBillingMessage(null);
    try {
      const res = await apiFetch(`${API_BASE}/billing/create_checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan, origin: window.location.origin }),
      });
      const data = await res.json();
      if (data.checkout_url) {
        if (checkoutWindow) {
          checkoutWindow.location.href = data.checkout_url;
        } else {
          window.location.href = data.checkout_url;
        }
        setBillingMessage({ success: true, msg: "支付页面已打开，付款完成后会员状态会自动刷新。" });
        for (let i = 1; i <= 24; i += 1) {
          setTimeout(() => refreshUserData(user.id), i * 5000);
        }
        return;
      }
      checkoutWindow?.close();
      setBillingMessage({ success: false, msg: data.msg || "创建支付订单失败" });
    } catch (error) {
      checkoutWindow?.close();
      setBillingMessage({ success: false, msg: `创建支付订单异常：${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  if (!authResolved) {
    return <SessionBootstrap isDark={isDark} />;
  }

  if (canvasNavigating) {
    return <SessionBootstrap isDark={isDark} />;
  }

  if (updatesNavigating) return <SessionBootstrap isDark={isDark} />;

  if (docsOpen) return <>
    <DocsPage isDark={isDark} onThemeToggle={toggleTheme} onBack={() => navigateDocs(null)} onUpdates={() => navigateUpdates(true)} />
    <RouteTransition transitionKey={routeTransitionKey} isDark={isDark} />
  </>;

  if (updatesOpen) {
    return <>
      <UpdatesPage isDark={isDark} onThemeToggle={toggleTheme} onBack={() => navigateUpdates(false)} onDocs={navigateDocs} backLabel={window.history.state?.returnToDocs ? "返回文档" : "返回对话"} />
      <RouteTransition transitionKey={routeTransitionKey} isDark={isDark} />
    </>;
  }

  if (
    view === "landing"
    || view === "login"
    || view === "register"
    || view === "bind-phone"
    || view === "phone-login"
    || view === "reset-password"
  ) {
    return (
      <>
        <AuthView
          view={view}
          isDark={isDark}
          onThemeToggle={toggleTheme}
          loading={loading}
          authForm={authForm}
          setAuthForm={setAuthForm}
          handleAuth={handleAuth}
          handleSendPhoneCode={handleSendPhoneCode}
          handleSendPasswordResetCode={handleSendPasswordResetCode}
          handleCompleteRegister={handleCompleteRegister}
          handlePhoneCodeLogin={handlePhoneCodeLogin}
          handlePasswordReset={handlePasswordReset}
          phoneCodeStatus={phoneCodeStatus}
          phoneCodeCooldown={phoneCodeCooldown}
          setView={setAuthView}
        />
        <RouteTransition transitionKey={routeTransitionKey} isDark={isDark} />
      </>
    );
  }

  if (profileOpen) return <div className={`app-shell ${isDark ? "theme-dark" : "theme-light"}`}>
    <ProfilePage user={user} loadActivity={loadAccountActivity} onBack={()=>navigateProfile(false)} />
    <RouteTransition transitionKey={routeTransitionKey} isDark={isDark} />
  </div>;

  const fetchedModelOptions = availableModels.map((model) => model?.id).filter(Boolean);
  const modelOptions = [...new Set(fetchedModelOptions)];

  return (
    <motion.div
      className={`app-shell ${isDark ? "theme-dark" : "theme-light"}`}
      initial={{ opacity: 0, y: 28, scale: 0.988 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.68, ease: [0.16, 1, 0.3, 1], delay: 0.08 }}
    >
      <RouteTransition transitionKey={routeTransitionKey} isDark={isDark} />
      {isRecording && (
        <VoiceLiveOverlay
          canceling={voiceCanceling}
          sending={voiceSending}
          overlayRef={voiceOverlayRef}
          cancelTargetRef={voiceCancelTargetRef}
          sendTargetRef={voiceSendTargetRef}
          waveformRef={voiceOverlayWaveformRef}
          transcriptRef={voiceLiveTextRef}
        />
      )}
      <Sidebar
        user={user}
        isDark={isDark}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        handleNewChat={handleNewChat}
        sessions={sessions}
        activeSessionId={activeSessionId}
        pendingSessionIds={pendingSessionIds}
        unreadSessionIds={unreadSessionIds}
        loadSession={(sessionId) => {
          loadSession(sessionId);
          closeSidebarOnMobile();
        }}
        deleteSession={deleteSession}
        onThemeToggle={toggleTheme}
        setActiveTab={setActiveTab}
        setIsSettingsOpen={(open) => {
          setIsSettingsOpen(open);
          if (open) closeSidebarOnMobile();
        }}
        setView={setView}
        onLogout={handleLogout}
        onOpenUpdates={() => navigateUpdates(true)}
        onOpenDocs={() => navigateDocs()}
        onProfile={() => navigateProfile(true)}
        webUsage={webUsage}
        apiUsage={apiUsage}
        onAccountMenuChange={setAccountMenuOpen}
      />

      <main className="chat-main">
        <header className="chat-header">
          <div className="header-left">
            {!sidebarOpen && (
              <button type="button" className="icon-button" onClick={() => setSidebarOpen(true)} aria-label="展开侧边栏">
                <PanelRightOpen size={20} />
              </button>
            )}
            <div className="rag-picker">
              <button type="button" onClick={() => setRagSelectorOpen(!ragSelectorOpen)} title="对话模式">
                {activeChatMode === "video_creation" ? <Video size={16} /> : <BookOpen size={16} />}
                <span>{ragLabel}</span>
                <ChevronDown size={15} className={ragSelectorOpen ? "rotate" : ""} />
              </button>
              {ragSelectorOpen && (
                <div className="rag-menu">
                  <button
                    type="button"
                    className={activeChatMode === "normal" && activeRag.mode === "none" ? "active" : ""}
                    onClick={() => updateActiveSessionRag({ mode: "none" })}
                    disabled={activeSessionPending}
                  >
                    <span>普通对话</span>
                    <small>日常协作与探索</small>
                  </button>
                  <button
                    type="button"
                    className={activeChatMode === "video_creation" ? "active" : ""}
                    onClick={() => updateActiveSessionMode("video_creation")}
                    disabled={activeSessionPending}
                  >
                    <span>视频创作</span>
                    <small>概念到成片</small>
                  </button>
                  <button
                    type="button"
                    onClick={openCanvas}
                  >
                    <span>无限画布</span>
                    <small>自由编排灵感</small>
                  </button>
                  <div className="rag-menu-divider" />
                  <button
                    type="button"
                    className={activeChatMode === "normal" && activeRag.mode === "all" ? "active" : ""}
                    onClick={() => updateActiveSessionRag({ mode: "all" })}
                    disabled={activeSessionPending}
                  >
                    全部知识库
                  </button>
                  <div className="rag-menu-divider" />
                  {ragCollections.map((collection) => (
                    <button
                      key={collection.collection_id}
                      type="button"
                      className={activeChatMode === "normal" && activeRag.collection_id === collection.collection_id ? "active" : ""}
                      onClick={() => updateActiveSessionRag({ mode: "collection", collection_id: collection.collection_id })}
                      disabled={activeSessionPending}
                    >
                      <span>{collection.name || "未命名知识库"}</span>
                      <small>{collection.status === "indexing" ? "向量化中" : `${collection.chunk_count || 0} 片段`}</small>
                    </button>
                  ))}
                  <button type="button" className="rag-upload-button" onClick={() => ragFileInputRef.current?.click()} disabled={isUploading}>
                    {isUploading ? <Loader2 className="spin" size={15} /> : <UploadCloud size={15} />}
                    上传知识库文件
                  </button>
                </div>
              )}
              <input
                type="file"
                ref={ragFileInputRef}
                className="hidden-input"
                accept=".txt,.md,.pdf,.docx,text/plain,text/markdown,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={(event) => uploadKnowledgeFile(event.target.files[0])}
              />
            </div>
          </div>
          <div className="header-right">
            <button
              type="button"
              className={`sound-toggle ${activeTtsEnabled ? "active" : ""} ${ttsPlaying ? "playing" : ""}`}
              onClick={toggleActiveTts}
              disabled={!activeSessionId}
              aria-label={activeTtsEnabled ? "关闭语音回复" : "打开语音回复"}
              title={activeTtsEnabled ? "关闭语音回复" : "打开语音回复"}
            >
              {activeTtsEnabled ? <Volume2 size={19} /> : <VolumeX size={19} />}
            </button>
          </div>
        </header>

        {ragUploadStatus && (
          <div className={`rag-status-toast ${ragUploadStatus.kind || "running"}`}>
            {ragUploadStatus.kind === "running" && <Loader2 className="spin" size={15} />}
            <span>{ragUploadStatus.message}</span>
            <button type="button" onClick={() => setRagUploadStatus(null)} aria-label="关闭知识库状态">
              <X size={14} />
            </button>
          </div>
        )}

        <section className="chat-scroll" ref={chatScrollRef}>
          <div className="message-list">
            {loadingSessionId === activeSessionId && (
              <div className="session-loading-state">
                <Loader2 className="spin" size={18} />
                <span>正在载入对话</span>
              </div>
            )}
            {chatHistory.length === 0 && loadingSessionId !== activeSessionId && (
              <div className="empty-state">
                {activeChatMode === "video_creation" ? <Video size={44} /> : <Sparkles size={44} />}
                <h1>{activeChatMode === "video_creation" ? "先确定这条视频讲什么" : "有什么可以帮您的？"}</h1>
                {activeChatMode === "video_creation" && (
                  <p>描述主题或灵感，先完成一分钟以内的视频概念，再逐步准备制作素材。</p>
                )}
              </div>
            )}
            {displayMessages.map((msg) => {
              const index = chatHistory.indexOf(msg);
              const messageKey = activeSessionId
                ? `${activeSessionId}:${msg.message_id || index}:${(msg.content || "").length}`
                : `${msg.role}-${msg.message_id || index}`;
              const canRetry = msg.role === "user"
                && chatHistory[index + 1]?.message_type === "interrupted";
              return (
                <React.Fragment key={msg.message_id || `${msg.role}-${index}`}>
                {msg.role === "assistant" && msg.execution?.activity?.steps?.length > 0 ? (
                  <div className="execution-summary-row"><ExecutionProgress job={msg.execution} labels={toolLabels} /></div>
                ) : !activeSessionPending && index === latestAssistantIndex
                  && msg.created_at >= Number(activeToolJob?.started_at || activeToolJob?.created_at || 0)
                  && (activeToolJob?.activity || activeToolJob?.usage?.activity)?.steps?.length > 0 && (
                  <div className="execution-summary-row"><ExecutionProgress job={activeToolJob} labels={toolLabels} /></div>
                )}
                <MessageItem
                  key={msg.message_id || `${msg.role}-${index}`}
                  msg={msg}
                  isDark={isDark}
                  animate={messageKey === animatedMessageKey}
                  animationDurationMs={messageKey === animatedMessageKey ? ttsPlayback?.durationMs : null}
                  actionsDisabled={activeSessionPending || !msg.message_id || msg.message_id.startsWith("temp_")}
                  canRetry={canRetry}
                  canEdit={index === latestUserMessageIndex}
                  onEdit={(message, nextContent) => regenerateMessage(message, nextContent)}
                  onRetry={(message) => regenerateMessage(message)}
                  onAnimationDone={() => {
                    setAnimatedMessageKey(null);
                    setTtsPlayback(null);
                  }}
                  onAnimationStep={() => scrollChatToBottom("auto")}
                />
                </React.Fragment>
              );
            })}
            {activeSessionPending && (
              <div className="message-row">
                <div className="assistant-avatar">
                  <img src={isDark ? ICON_CONFIG.botAvatarDark : ICON_CONFIG.botAvatarLight} alt="" />
                </div>
                <ExecutionProgress job={activeToolJob} running labels={toolLabels} onUpdate={keepExecutionInView} />
              </div>
            )}
            <div ref={chatEndRef} className="scroll-anchor" />
          </div>
        </section>

        <footer className="composer-wrap">
          <ComposerQueue items={composerQueue.items} enabled={composerQueue.enabled} busy={queueBusy} running={canSteerTask} onEditingChange={setQueueEditing}
            onGuide={item => deliverQueuedMessage(item, "guide")} onSend={item => deliverQueuedMessage(item, "send")}
            onEdit={(item, content) => updateComposerQueue(previous => ({ ...previous, items: previous.items.map(value => value.id === item.id && value.state !== "uncertain"
              ? { ...value, content, state: "pending", attempt: null, id: value.state === "blocked" ? window.crypto.randomUUID() : value.id } : value) }))}
            onRemove={item => updateComposerQueue(previous => ({ ...previous, items: previous.items.filter(value => value.id !== item.id) }))}
            onToggle={() => updateComposerQueue(previous => ({ ...previous, enabled: !previous.enabled }))} />
          {previewUrl && (
            <div className="upload-preview">
              <img src={previewUrl} alt="预览" />
              <button
                type="button"
                onClick={() => {
                  setUploadedPath(null);
                  setPreviewUrl(null);
                }}
                aria-label="移除图片"
              >
                <X size={16} />
              </button>
            </div>
          )}
          <div className="composer">
            <button
              type="button"
              className="icon-button composer-attach"
              onClick={() => fileInputRef.current?.click()}
              aria-label="上传图片"
            >
              <Paperclip size={21} />
            </button>
            <input
              type="file"
              ref={fileInputRef}
              className="hidden-input"
              accept="image/*"
              onChange={(event) => uploadImage(event.target.files[0])}
            />
            {voiceMode ? (
              <div className="voice-composer" role="group" aria-label="语音输入">
                <button
                  type="button"
                  className={`voice-hold-button ${isRecording ? "recording" : ""} ${voiceCanceling ? "canceling" : ""}`}
                  onPointerDown={handleVoicePointerDown}
                  onPointerMove={handleVoicePointerMove}
                  onPointerUp={handleVoicePointerUp}
                  onPointerCancel={() => stopVoiceCapture({ cancel: true })}
                  onKeyDown={handleVoiceKeyDown}
                  onKeyUp={handleVoiceKeyUp}
                  onContextMenu={(event) => event.preventDefault()}
                  disabled={isVoiceFinalizing}
                  aria-label={isVoiceFinalizing ? "正在完成语音识别" : "按住说话，松开结束"}
                >
                  <span className="voice-hold-icon">
                    {isVoiceFinalizing ? <Loader2 className="spin" size={19} /> : isRecording ? <MicOff size={19} /> : <Mic size={19} />}
                  </span>
                  <span ref={voiceWaveformRef} className={`voice-waveform ${isRecording ? "active" : ""}`} aria-hidden="true">
                    {Array.from({ length: 24 }, (_, index) => <span key={index} />)}
                  </span>
                  <span className="voice-hold-copy">
                    <strong>{isVoiceFinalizing ? "正在整理" : voiceCanceling ? "松开取消" : isRecording ? "松开结束" : "按住说话"}</strong>
                    <small>{chatMessage.trim() || "语音会转换成文字"}</small>
                  </span>
                </button>
                <button
                  type="button"
                  className="icon-button voice-close-button"
                  onClick={closeVoiceMode}
                  disabled={isRecording || isVoiceFinalizing}
                  aria-label="退出语音输入"
                  title="退出语音输入"
                >
                  <X size={18} />
                </button>
              </div>
            ) : (
              <textarea
                ref={textareaRef}
                rows="1"
                placeholder={activeChatMode === "video_creation" ? "描述你想做的短视频，没想法也可以让我来设计..." : "输入消息..."}
                value={chatMessage}
                onCompositionStart={() => {
                  isComposingRef.current = true;
                }}
                onCompositionEnd={() => {
                  isComposingRef.current = false;
                }}
                onFocus={() => {
                  keepComposerVisible();
                }}
                onChange={(event) => {
                  setChatMessage(event.target.value);
                  event.target.style.height = "auto";
                  event.target.style.height = `${Math.min(event.target.scrollHeight, 220)}px`;
                  keepComposerVisible();
                }}
                onKeyDown={(event) => {
                  const isComposing = isComposingRef.current || event.nativeEvent.isComposing || event.keyCode === 229;
                  if (event.key === "Enter" && !event.shiftKey && !isComposing) {
                    event.preventDefault();
                    handleChat();
                  }
                }}
              />
            )}
            <ModelPicker value={sandboxConfig.model} models={modelOptions}
              onChange={model => setSandboxConfig(previous => ({ ...previous, model }))} />
            {(canSteerTask || !composerQueue.enabled) && <button type="button" className="icon-button queue-mode-toggle"
              aria-pressed={composerQueue.enabled} aria-label={composerQueue.enabled ? "关闭消息排队" : "开启消息排队"}
              title={composerQueue.enabled ? "关闭消息排队" : "开启消息排队"}
              onClick={() => updateComposerQueue(previous => ({ ...previous, enabled: !previous.enabled }))}>
              <ListOrdered size={18} />
            </button>}
            {!voiceMode && (
              <button
                type="button"
                className={`icon-button voice-button ${isVoiceFinalizing ? "finalizing" : ""}`}
                onClick={openVoiceMode}
                disabled={isVoiceFinalizing}
                aria-label="打开语音输入"
                title="打开语音输入"
              >
                {isVoiceFinalizing ? <Loader2 className="spin" size={20} /> : <Mic size={20} />}
              </button>
            )}
            {steeringDraft && <button type="button" className="icon-button task-stop-button" onClick={handleInterrupt}
              disabled={Boolean(interruptingJobId)} aria-label="停止生成" title="停止生成">
              <Square size={15} fill="currentColor" />
            </button>}
            <button
              type="button"
              className={`send-button ${activeSessionPending && !steeringDraft ? "stop-generation" : ""}`}
              onClick={activeSessionPending && !steeringDraft ? handleInterrupt : handleChat}
              disabled={
                isRecording
                || isVoiceFinalizing
                || isUploading
                || followupSending
                || (activeSessionPending
                  ? !activeSessionJob?.job_id || Boolean(interruptingJobId)
                  : (!chatMessage.trim() && !uploadedPath))
              }
              aria-label={steeringDraft ? composerQueue.enabled ? "加入排队" : "追加指令" : activeSessionPending ? "停止生成" : "发送"}
              title={steeringDraft ? composerQueue.enabled ? "加入排队" : "追加指令" : activeSessionPending ? "停止生成" : "发送"}
            >
              {interruptingJobId
                ? <Loader2 className="spin" size={20} />
                : activeSessionPending && !steeringDraft
                  ? <Square size={15} fill="currentColor" />
                  : <Send size={21} />}
            </button>
          </div>
          {voiceStatus && <p className="voice-status" role="status">{voiceStatus}</p>}
          <p className="composer-note">
            LumaNova 可能产生错误信息，请核实重要内容。
          </p>
        </footer>
      </main>

      <UpdateNotice open={noticeOpen} onClose={() => setNoticeOpen(false)}
        onMute={() => { muteAnnouncements(user?.id); setNoticeOpen(false); }}
        onDetails={() => navigateUpdates(true)} />

      <SettingsModal
        isOpen={isSettingsOpen}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        keys={keys}
        tokenBalance={tokenBalance}
        webUsage={webUsage}
        apiUsage={apiUsage}
        loading={loading}
        newKeyData={newKeyData}
        copySuccess={copySuccess}
        setCopySuccess={setCopySuccess}
        setNewKeyData={setNewKeyData}
        setIsSettingsOpen={setIsSettingsOpen}
        createKey={createKey}
        deleteKey={deleteKey}
        changePassword={changePassword}
        createCheckout={createCheckout}
        billingMessage={billingMessage}
        themeMode={themeMode}
        onThemeModeChange={chooseThemeMode}
      />

      {profilePrompt.open && (
        <div className="modal-backdrop">
          <section className="password-prompt" role="dialog" aria-modal="true" aria-label="完善账号信息">
            <button type="button" className="prompt-close" onClick={() => setProfilePrompt((prev) => ({ ...prev, open: false }))} aria-label="关闭">
              <X size={18} />
            </button>
            <h2>完善账号信息</h2>
            <p>
              {profilePrompt.needsUsername ? "手机号验证码登录已完成，请设置一个正式用户名。" : ""}
              {profilePrompt.needsPassword ? " 建议设置密码，之后可以用手机号或用户名加密码登录。" : ""}
            </p>
            <div className="password-fields">
              {profilePrompt.needsUsername && (
                <input
                  placeholder="用户名"
                  value={profileForm.username}
                  onChange={(event) => setProfileForm({ ...profileForm, username: event.target.value })}
                />
              )}
              {profilePrompt.needsPassword && (
                <>
                  <input
                    type="password"
                    placeholder="设置密码"
                    value={profileForm.password}
                    onChange={(event) => setProfileForm({ ...profileForm, password: event.target.value })}
                  />
                  <input
                    type="password"
                    placeholder="确认密码"
                    value={profileForm.confirmPassword}
                    onChange={(event) => setProfileForm({ ...profileForm, confirmPassword: event.target.value })}
                  />
                </>
              )}
            </div>
            {profileMessage?.msg && (
              <p className={`auth-message ${profileMessage.success ? "success" : "error"}`}>{profileMessage.msg}</p>
            )}
            <button
              type="button"
              className="primary-button"
              onClick={handleSaveProfile}
              disabled={
                loading ||
                (profilePrompt.needsUsername && !profileForm.username.trim()) ||
                (profilePrompt.needsPassword && (!profileForm.password || profileForm.password !== profileForm.confirmPassword))
              }
            >
              {loading ? <Loader2 className="spin" size={18} /> : "保存"}
            </button>
            <button type="button" className="link-button prompt-skip" onClick={() => setProfilePrompt((prev) => ({ ...prev, open: false }))}>
              稍后再说
            </button>
          </section>
        </div>
      )}
    </motion.div>
  );
}
